import { isGDSSupplier } from "../supplier/getSupplierName";
import {
  isRouteChangeRequest,
  parseChangeSelectedOffer,
} from "./getRequestedDateChange";

export const CHANGE_ACTION = {
  accept: "accept",
  review: "review",
  assign: "assign",
  decline: "decline",
  quote: "quote",
  adminPay: "adminPay",
  reviewBankProof: "reviewBankProof",
  submit: "submit",
  manualChange: "manualChange",
  confirmReissue: "confirmReissue",
  gdsReissue: "gdsReissue",
  retryAuto: "retryAuto",
  remark: "remark",
  fareRules: "fareRules",
  messages: "messages",
  viewSelectedOffer: "viewSelectedOffer",
  startOpen: "startOpen",
  proceedOpen: "proceedOpen",
  notEligible: "notEligible",
  recordOpened: "recordOpened",
  airlineDeclined: "airlineDeclined",
  processingFailed: "processingFailed",
  needMoreInfo: "needMoreInfo",
  supplierResponse: "supplierResponse",
};

export function hasChangeSupplierResponse(data) {
  const response = data?.supplierResponse;
  if (response == null) return false;
  if (typeof response === "object") {
    return Object.keys(response).length > 0;
  }
  return true;
}

export function isDetailsChange(data) {
  return data?.changeRequest === "PassengerInfo";
}

export function isOpenTicketChange(data) {
  return data?.changeRequest === "Open";
}

export function canViewChangeSelectedOffer(data) {
  if (!data || isOpenTicketChange(data) || isDetailsChange(data)) return false;
  return Boolean(parseChangeSelectedOffer(data));
}

function normalizeChangeStatus(status) {
  return String(status || "")
    .replace(/[_\s-]/g, "")
    .toLowerCase();
}

export function getChangeStatusCode(status) {
  return normalizeChangeStatus(status);
}

function changeStatusNote(data) {
  return String(
    data?.failureReason ||
      data?.manualInterventionReason ||
      data?.lastError ||
      data?.errorMessage ||
      data?.rejectReason ||
      "",
  );
}

export function hasAutoDateChangeFailedInHistory(data) {
  const history = Array.isArray(data?.statusHistory) ? data.statusHistory : [];
  return history.some((entry) => isAutoDateChangeFailedStatus(entry?.status));
}

/** AutoDateChangeFailed or AutoChangeSubmissionFailed — auto path already failed once. */
export function hasChangeAutoFailureInHistory(data) {
  const history = Array.isArray(data?.statusHistory) ? data.statusHistory : [];
  return history.some((entry) => {
    const code = normalizeChangeStatus(entry?.status);
    return (
      code === "autodatechangefailed" ||
      code === "autochangesubmissionfailed"
    );
  });
}

/**
 * Retry POST /changeBooking/reissue when auto failed and Ops is on ManualReviewRequired.
 * Applies to date change, details change, and open ticket when AutoDateChangeFailed
 * (or AutoChangeSubmissionFailed) is in statusHistory. API still requires selectedOffer
 * and an in-place acceptance for a successful retry.
 */
export function canRetryAutoChange(data) {
  if (!data) return false;
  if (normalizeChangeStatus(data?.status) !== "manualreviewrequired") {
    return false;
  }
  return hasChangeAutoFailureInHistory(data);
}

export function getChangeRetryAutoActionLabel() {
  return "Retry auto submission";
}

export function getChangeRetryAutoConfirmLabel() {
  return "Yes, retry automatic submission";
}

export function getChangeRetryAutoLoadingTitle() {
  return "Retrying automatic submission";
}

export function getChangeRetryAutoWarningCopy(data) {
  const kind = getChangeKindLabel(data);
  return {
    phase: "warn",
    kind: "retry_auto_warn",
    title: "Retry automatic submission?",
    text: `This asks Intraverse to submit this paid ${kind} to the supplier again (POST reissue). Use it when auto already failed (AutoDateChangeFailed / send failure) and acceptance is still in place. It does not finish the ticket by itself. Stay on this screen while it runs. A timeout is not an airline decline.`,
  };
}

export function getChangeRetryAutoCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "retry_autoed",
      title: "Automatic submission started again",
      text: "Intraverse is retrying the supplier submission. Wait for the status to update. Do not submit a second change on the airline site unless this fails again.",
    };
  }
  return {
    phase: "error",
    kind: "retry_auto_failed",
    title: "Automatic submission retry failed",
    text: `${msg || "The request failed or timed out"}. That is not an airline decline. You can try auto again.`,
  };
}

export function getChangeFailureReason(data) {
  const reason = String(data?.failureReason || "").trim();
  return reason || "";
}

function looksLikeChangeSendFailure(data) {
  const kind = String(data?.lastErrorKind || "").toLowerCase();
  if (["submission", "send", "exchange", "reissue"].includes(kind)) return true;
  return /could not send|send failed|submission failed|failed to send|failed to submit|unable to send|unable to submit|exchange failed|reissue failed/i.test(
    changeStatusNote(data),
  );
}

function looksLikeChangePriceFailure(data) {
  const kind = String(data?.lastErrorKind || "").toLowerCase();
  if (["pricing", "price", "quote"].includes(kind)) return true;
  return /pric|quote|repric|fare/i.test(changeStatusNote(data));
}

export function isChangePaymentSecured(data) {
  if (!data) return false;
  // Status only — never isConfirmed / isPaid booleans.
  if (isChangePaidStatus(data?.status)) return true;
  const payment = normalizeChangeStatus(data?.paymentStatus);
  if (
    [
      "paid",
      "authorised",
      "authorized",
      "paymentauthorised",
      "paymentauthorized",
      "paymentnotrequired",
    ].includes(payment)
  ) {
    return true;
  }
  const history = Array.isArray(data?.statusHistory) ? data.statusHistory : [];
  return history.some((entry) => isChangePaidStatus(entry?.status));
}

/** Agent uploaded bank transfer proof that ops has not confirmed yet. */
export function hasPendingChangeBankTransferProof(data) {
  const list = Array.isArray(data?.bankTransferProofs)
    ? data.bankTransferProofs
    : [];
  return list.some((p) => String(p?.status || "").toLowerCase() === "pending");
}

function getManualReviewIntent(data) {
  if (isChangePaymentSecured(data) || looksLikeChangeSendFailure(data)) {
    return "send";
  }
  if (
    isChangeAlreadyAccepted(data) &&
    hasChangeQuote(data) &&
    isChangeTechnicalSendFailure(data)
  ) {
    return "send";
  }
  if (hasChangeQuote(data)) return "pay";
  if (
    isChangeAlreadyAccepted(data) ||
    looksLikeChangePriceFailure(data) ||
    isChangeTechnicalSendFailure(data)
  ) {
    return "get_price";
  }
  return "review";
}

export function getChangeReviewTrigger(data) {
  const status = normalizeChangeStatus(data?.status);
  const intent = getManualReviewIntent(data);

  if (status === "autochangesubmissionfailed") return "send_failed";
  if (status === "autodatechangefailed") return "quote_failed";
  if (status === "namechangerequestsubmitted") {
    return "eligibility_review";
  }
  if (status === "datechangerequestsubmitted") {
    return "request_submitted";
  }
  if (status === "autodatechangeprocessing") return "waiting_for_price";
  if (
    status === "datechangevalidationfailed" ||
    status === "namechangevalidationfailed"
  ) {
    return "validation_failed";
  }
  if (
    status === "manualdatechangeprocessing" ||
    status === "manualnamechangeprocessing"
  ) {
    // Status alone — Ops quote. Never "send" from leftover payment flags.
    return "manual_quote";
  }
  if (status === "manualreviewrequired" || status === "pending") {
    // Always eligibility review while the API status says so.
    return "eligibility_review";
  }
  if (intent === "send") return "send_failed";
  if (intent === "get_price") return "quote_failed";
  return "eligibility_review";
}

function changeStatusDisplay(data) {
  return data?.status || "Unknown";
}

export function getChangeReviewGuidance(data) {
  const kind = getChangeKindLabel(data);
  const trigger = getChangeReviewTrigger(data);
  const note = changeStatusNote(data);
  const isDetails = isDetailsChange(data);
  const what = isDetails ? "passenger details" : "dates";
  const airlineCheck = isDetails
    ? "On the airline site, check if these name changes are allowed on this ticket."
    : "On the airline site, check if these new dates are allowed on this fare.";
  const statusDisplay = changeStatusDisplay(data);

  const shared = {
    trigger,
    kind,
    note,
    statusDisplay,
    whyTitle: "Why you're here",
    showDecline: true,
    showAccept: false,
    showQuote: false,
    showSubmit: false,
    showManual: false,
    acceptLabel: "Accept — then enter quote",
    declineLabel: "Decline",
    quoteLabel: getChangeQuoteActionLabel(data),
    submitLabel: "Confirm sent to airline",
    manualLabel: "Manual change",
  };

  const copies = {
    send_failed: {
      title: "Submission to the airline failed",
      why: `The accepted ${kind} was not sent to the airline. This is a technical send failure (timeout, API down, or unsupported exchange). It is not an airline decline, and it is not a quote failure. The original ticket is unchanged.`,
      steps: [
        "Open the airline or supplier site.",
        "Find this issued ticket and submit the exchange by hand.",
        "Come back and click Confirm sent to airline.",
        "Use Manual change only if the airline already finished the change.",
      ],
      questions: [
        "Did sending fail on our side, or did the airline actually say no?",
        "If it was our send, did you finish it on the airline site?",
        "A timeout is not an airline no.",
      ],
      showDecline: false,
      showSubmit: true,
      showManual: true,
    },
    quote_failed: {
      title: "Getting a quote from the airline failed",
      why: `Intraverse asked the supplier for a ${kind} price and did not get one. This is a pricing lookup failure (timeout, API down, or unsupported price request). It is not a failed ticket submission, and it is not an airline decline. No exchange was sent.`,
      steps: [
        "Open the airline or supplier site.",
        "Find this issued ticket.",
        airlineCheck,
        "Get the new itinerary and the airline price.",
        'Come back and click "Enter quote from airline". Enter final amounts the agent should pay (markup fare difference only — change fee & penalty at airline cost). Service fee is added from fee config.',
      ],
      questions: [
        "Did you get a price on the airline site, or did the airline refuse the change?",
        `If there is a price, enter the quote. If the fare cannot change ${what}, Decline.`,
      ],
      showQuote: true,
      showDecline: true,
    },
    request_submitted: {
      title: "Request passed checks — get the airline price",
      why: `Validation passed. The next step in the flow is supplier pricing, not Accept/Decline. The computer should request a ${kind} price. If you are here, that price has not come back yet.`,
      steps: [
        "Wait if the price is still loading.",
        "If it sits, open the airline or supplier site.",
        "Get the new itinerary and price.",
        "Enter the quote. Do not send the ticket change yet.",
      ],
      questions: [
        "Is a supplier price back yet?",
        "If not, can you get it on the airline site?",
      ],
      showQuote: true,
      showDecline: true,
    },
    waiting_for_price: {
      title: "Intraverse is asking the airline for a price",
      why: `The request is in automatic pricing. Intraverse is asking the supplier for a ${kind} quote. This is not a failed send, and not an airline decline.`,
      steps: [
        "Wait for the supplier price.",
        "If this sits too long, take over: open the airline site, get the price, and enter the quote.",
      ],
      questions: ["Has the supplier returned a price yet?"],
      showQuote: true,
      showDecline: true,
    },
    validation_failed: {
      title: "This request could not be started",
      why: `A required check failed before pricing or send. There is no issued-ticket exchange to do. No money moved.`,
      steps: [
        "Read the validation reason.",
        "Close this request.",
        "The agent can submit again if the ticket is still eligible.",
      ],
      questions: ["Which check failed?"],
      showDecline: true,
      declineLabel: "Close this request",
    },
    manual_quote: {
      title: "Get the airline price by hand, then enter the quote",
      why: `This ${kind} is in manual pricing. The supplier is not being priced automatically. You must check the airline site for the price first — then type that quote into Intraverse. Do not invent amounts.`,
      steps: [
        "Open the airline or supplier site.",
        "Find this issued ticket.",
        airlineCheck,
        "Write down the airline price (fee / fare difference / penalty).",
        'Come back and click "Enter quote from airline". Enter final agent amounts (markup fare difference only — change fee & penalty at cost). Service fee comes from fee config. Manual quotes do not run post-ticket markup rules.',
      ],
      questions: [
        `Can this ticket change ${what} on this fare?`,
        "Do you have the airline price written down?",
      ],
      showQuote: true,
      showDecline: true,
    },
    manual_send: {
      title: "Ops must send this to the airline by hand",
      why: `The quote was accepted. Automatic send is not available. Submit the exchange on the airline site. Do not create a new quote on this same request.`,
      steps: [
        "Open the airline or supplier site.",
        "Submit the exchange for this issued ticket.",
        "Confirm sent, or use Manual change only if the airline already finished it.",
      ],
      questions: ["Has the airline already completed the exchange?"],
      showDecline: false,
      showSubmit: true,
      showManual: true,
    },
    eligibility_review: {
      title: `Check if this ${kind} is allowed`,
      why: isManualReviewAfterAutoDateChangeFailed(data)
        ? `Automatic ${kind} pricing failed, so Ops must review this request. It is not a failed ticket submission and not an airline decline. Check the airline site, then Accept (enter a quote next) or Decline.`
        : `The platform could not automatically confirm this request is valid, or this supplier does not support automatic ${kind} pricing. That is the Manual Review step. It is not a failed ticket submission. It is not an airline decline. You must check the airline site first.`,
      steps: [
        "Open the airline or supplier site.",
        "Find this issued ticket.",
        airlineCheck,
        "If the airline can do it, Accept. You will enter a quote next. Accepting is not a quote.",
        "If the airline cannot, Decline and write a plain-language reason.",
      ],
      questions: [
        "Did you check this on the airline or supplier site?",
        "Is this the right issued ticket?",
        `Can this ticket change ${what} on this fare?`,
        "If yes, Accept. If no, Decline.",
      ],
      showAccept: true,
      showDecline: true,
    },
  };

  return { ...shared, ...(copies[trigger] || copies.eligibility_review) };
}

export function isChangeManualReviewStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return (
    normalized === "pending" ||
    normalized === "manualreviewrequired" ||
    normalized === "openticketrequestsubmitted" ||
    normalized === "namechangerequestsubmitted"
  );
}

export function isChangeRefundDueStatus(status) {
  return normalizeChangeStatus(status) === "refunddue";
}

export function isChangeAlreadyAccepted(data) {
  const normalized = normalizeChangeStatus(data?.status);
  return (
    data?.status === "Approved" ||
    normalized === "datechangeaccepted" ||
    normalized === "datechangequoted" ||
    normalized === "namechangequoted" ||
    normalized === "namechangepaymentrequired" ||
    normalized === "namechangepaymentauthorised" ||
    normalized === "namechangepaymentauthorized"
  );
}

export function needsChangeReview(data) {
  if (!data || isOpenTicketChange(data)) return false;
  return getChangeAdminStage(data) === "needs_review";
}

export const CHANGE_REVIEW_STATUSES = [
  "Pending",
  "ManualReviewRequired",
  "MANUAL_REVIEW_REQUIRED",
  "NameChangeRequestSubmitted",
  "NAME_CHANGE_REQUEST_SUBMITTED",
];

export const OPEN_TICKET_REVIEW_STATUSES = [
  "Pending",
  "ManualReviewRequired",
  "MANUAL_REVIEW_REQUIRED",
  "OpenTicketRequestSubmitted",
  "OPEN_TICKET_REQUEST_SUBMITTED",
];

export function needsOpenTicketReview(data) {
  if (!data || !isOpenTicketChange(data)) return false;
  if (isChangeAlreadyAccepted(data)) return false;
  const status = normalizeChangeStatus(data?.status);
  return (
    status === "pending" ||
    status === "manualreviewrequired" ||
    status === "openticketrequestsubmitted"
  );
}

export const OPEN_TICKET_PROCESSING_STATUSES = [
  "ManualOpenTicketProcessing",
  "MANUAL_OPEN_TICKET_PROCESSING",
];

export function isOpenTicketProcessingStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return normalized === "manualopenticketprocessing";
}

export function needsOpenTicketProcessing(data) {
  if (!data || !isOpenTicketChange(data)) return false;
  return isOpenTicketProcessingStatus(data?.status);
}

export const OPEN_TICKET_SUPPLIER_STATUSES = [
  "OpenTicketProcessingWithSupplier",
  "OPEN_TICKET_PROCESSING_WITH_SUPPLIER",
];

export function isOpenTicketSupplierStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return normalized === "openticketprocessingwithsupplier";
}

export function needsOpenTicketSupplierRecord(data) {
  if (!data || !isOpenTicketChange(data)) return false;
  return isOpenTicketSupplierStatus(data?.status);
}

export const OPEN_TICKET_OUTCOME = {
  SUCCESS: "Successful",
  NOT_ELIGIBLE: "NotEligible",
  DECLINED_BY_SUPPLIER: "DeclinedBySupplier",
  PROCESSING_FAILED: "ProcessingFailed",
  ADDITIONAL_INFO: "AdditionalInformationRequired",
};

export function getChangeKindLabel(data, { plural = false } = {}) {
  if (isOpenTicketChange(data)) {
    return plural ? "open tickets" : "open ticket";
  }
  if (isDetailsChange(data)) {
    return plural ? "details changes" : "details change";
  }
  if (isRouteChangeRequest(data)) {
    return plural ? "route changes" : "route change";
  }
  return plural ? "date changes" : "date change";
}

export function isChangeQuotedStatus(status) {
  const code = normalizeChangeStatus(status);
  return (
    code === "datechangequoted" ||
    code === "datechangeaccepted" ||
    code === "paymentrequired" ||
    code === "datechangequoteexpired" ||
    code === "namechangequoted" ||
    code === "namechangepaymentrequired" ||
    code === "namechangequoteexpired"
  );
}

function readCachedChangeQuote(orderId) {
  if (!orderId || typeof sessionStorage === "undefined") return null;
  try {
    return JSON.parse(
      sessionStorage.getItem(`changeQuoteAdmin:${orderId}`) || "null",
    );
  } catch (_) {
    return null;
  }
}

export function hasChangeQuote(data) {
  if (isChangeQuotedStatus(data?.status)) return true;
  if (data?.isQuoted) return true;
  if (data?.changePrice || data?.changeFee) return true;
  if (Array.isArray(data?.priceBreakdown) && data.priceBreakdown.length) {
    return true;
  }
  const payable = Number(data?.payable);
  if (Number.isFinite(payable) && payable > 0) return true;
  // Name change often stays ManualNameChangeProcessing after setChangePrice —
  // treat a local admin quote cache as quoted so UI moves to awaiting payment.
  const cached = readCachedChangeQuote(data?._id);
  if (
    cached &&
    (Array.isArray(cached.priceBreakdown)
      ? cached.priceBreakdown.length > 0
      : Number(cached.intraverseServiceFee) > 0 ||
        Number(cached.sellingFareDifference) > 0 ||
        Number(cached.airlineChangePrice) > 0)
  ) {
    return true;
  }
  return false;
}

export function isZeroPayableChange(data) {
  if (!hasChangeQuote(data)) return false;
  const amount = Number(data?.payable);
  return Number.isFinite(amount) && amount <= 0;
}

export function isChangeTechnicalSendFailure(data) {
  const kind = String(data?.lastErrorKind || "").toLowerCase();
  if (kind === "technical") return true;
  return /technical error|api key|timeout|unavailable/i.test(
    String(data?.manualInterventionReason || ""),
  );
}

export function isChangePaidStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return (
    normalized === "paid" ||
    normalized === "paymentauthorised" ||
    normalized === "paymentauthorized" ||
    normalized === "paymentnotrequired" ||
    normalized === "datechangesubmitting" ||
    normalized === "namechangepaymentauthorised" ||
    normalized === "namechangepaymentauthorized"
  );
}

export function isChangeValidationFailedStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return (
    normalized === "datechangevalidationfailed" ||
    normalized === "openticketvalidationfailed" ||
    normalized === "namechangevalidationfailed"
  );
}

export function isAutoDateChangeProcessingStatus(status) {
  return normalizeChangeStatus(status) === "autodatechangeprocessing";
}

export function isChangeRequestSubmittedStatus(status) {
  const code = normalizeChangeStatus(status);
  return (
    code === "datechangerequestsubmitted" ||
    code === "namechangerequestsubmitted"
  );
}

export function isAutoDateChangeFailedStatus(status) {
  return normalizeChangeStatus(status) === "autodatechangefailed";
}

/** ManualReviewRequired after AutoDateChangeFailed — still Ops review; show why auto failed. */
export function isManualReviewAfterAutoDateChangeFailed(data) {
  const status = normalizeChangeStatus(data?.status);
  if (status !== "manualreviewrequired" && status !== "pending") return false;
  return hasAutoDateChangeFailedInHistory(data);
}

export function isManualChangeProcessingStatus(status) {
  const code = normalizeChangeStatus(status);
  return (
    code === "manualdatechangeprocessing" ||
    code === "manualnamechangeprocessing"
  );
}

export function isChangeSubmittedToSupplierStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return (
    normalized === "submittedtosupplier" ||
    normalized === "supplierprocessing" ||
    normalized === "autochangesubmission" ||
    normalized === "namechangesubmittedtosupplier"
  );
}

export function isChangeSendFailedStatus(status) {
  return normalizeChangeStatus(status) === "autochangesubmissionfailed";
}

export function isChangeSupplierFailedStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return (
    normalized === "datechangedeclinedbysupplier" ||
    normalized === "datechangefailedbysupplier"
  );
}

export function isChangeSettlementFailedStatus(status) {
  return normalizeChangeStatus(status) === "settlementfailed";
}

export function isChangeSettlementPendingStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return (
    normalized === "settlementpending" ||
    normalized === "datechangesuccessful" ||
    normalized === "additionalcollectiondebited" ||
    normalized === "balancerefunded" ||
    normalized === "nofinancialadjustment"
  );
}

export function isChangeFailureSettledStatus(status) {
  return normalizeChangeStatus(status) === "datechangefailuresettled";
}

export function isChangeFailureSettlementPendingStatus(status) {
  const normalized = normalizeChangeStatus(status);
  return (
    normalized === "settlementreversalpending" ||
    normalized === "paymentreservationreleased" ||
    normalized === "paymentreversed"
  );
}

export function isChangeQuoteExpiredStatus(status) {
  const code = normalizeChangeStatus(status);
  return code === "datechangequoteexpired" || code === "namechangequoteexpired";
}

/**
 * Explicit quote-expired statuses are terminal — no re-quote on the same request.
 * (Soft-expired DateChangeQuoted can still re-quote; DateChangeQuoteExpired cannot.)
 */
export function isChangeQuoteExpiredTerminal(status) {
  const code = normalizeChangeStatus(status);
  return code === "namechangequoteexpired" || code === "datechangequoteexpired";
}

/** @deprecated use isChangeQuoteExpiredTerminal */
export function isNameChangeQuoteExpiredTerminal(status) {
  return isChangeQuoteExpiredTerminal(status);
}

export function isChangeDeclinedByAgentStatus(status) {
  const code = normalizeChangeStatus(status);
  return (
    code === "datechangedeclinedbyagent" ||
    code === "datechangerequestcanceled" ||
    code === "datechangerequestcancelled" ||
    code === "namechangerequestcanceled" ||
    code === "namechangerequestcancelled"
  );
}

export function isChangeWithdrawnByAgentStatus(status) {
  const code = normalizeChangeStatus(status);
  return (
    code === "datechangerequestcanceled" ||
    code === "datechangerequestcancelled" ||
    code === "namechangerequestcanceled" ||
    code === "namechangerequestcancelled"
  );
}

export function isChangeDeclinedByAdminStatus(status) {
  const code = normalizeChangeStatus(status);
  return (
    code === "datechangerequestdenied" ||
    code === "namechangerequestdenied" ||
    code === "denied" ||
    code === "rejected"
  );
}

export function isOpenTicketNeedsInfoStatus(status) {
  return (
    normalizeChangeStatus(status) === "additionalinformationrequired" ||
    normalizeChangeStatus(status) === "openticketadditionalinformationrequired"
  );
}

export const CHANGE_SUBMIT_STATUSES = [
  "Paid",
  "PAYMENT_AUTHORISED",
  "PAYMENT_AUTHORIZED",
  "PAYMENT_NOT_REQUIRED",
  "DATE_CHANGE_SUBMITTING",
  "AUTO_CHANGE_SUBMISSION",
  "AUTO_CHANGE_SUBMISSION_FAILED",
  "AutoChangeSubmissionFailed",
  "NameChangePaymentAuthorised",
  "NAME_CHANGE_PAYMENT_AUTHORISED",
  "NameChangePaymentAuthorized",
  "NAME_CHANGE_PAYMENT_AUTHORIZED",
];

export function isChangeQuoteExpired(data) {
  // Once paid / authorised, the quote hold no longer applies.
  // NameChangeQuoted, PaymentRequired, NameChangePaymentRequired still
  // respect paymentDeadline / quoteExpiresAt.
  const code = normalizeChangeStatus(data?.status);
  if (
    isChangePaidStatus(data?.status) ||
    data?.status === "Paid" ||
    code === "namechangepaymentauthorised" ||
    code === "namechangepaymentauthorized" ||
    code === "paymentauthorised" ||
    code === "paymentauthorized"
  ) {
    return false;
  }
  const raw =
    data?.requestExpirationTime ||
    data?.quoteExpiresAt ||
    data?.paymentDeadline;
  if (!raw) return false;
  const expiry = new Date(raw);
  if (Number.isNaN(expiry.getTime())) return false;
  return expiry.getTime() < Date.now();
}

export function getChangeAdminStage(data) {
  if (isOpenTicketChange(data)) {
    const normalized = normalizeChangeStatus(data?.status);
    if (
      normalized === "denied" ||
      normalized === "rejected" ||
      normalized === "cancelled" ||
      normalized === "canceled" ||
      normalized === "openticketdeclinedbysupplier" ||
      normalized === "openticketrequestcanceled" ||
      normalized === "openticketrequestcancelled" ||
      normalized === "namechangerequestcanceled" ||
      normalized === "namechangerequestcancelled"
    ) {
      return "declined";
    }
    if (isChangeValidationFailedStatus(data?.status)) {
      return "open_validation_failed";
    }
    if (normalized === "openticketnoteligible") {
      return "open_not_eligible";
    }
    if (
      normalized === "openticketprocessingfailed" ||
      normalized === "failed"
    ) {
      return "open_failed";
    }
    if (isOpenTicketNeedsInfoStatus(data?.status)) {
      return "open_needs_info";
    }
    if (
      normalized === "openticketsuccessful" ||
      normalized === "openticketcompleted" ||
      normalized === "success" ||
      normalized === "closed"
    ) {
      return "open_completed";
    }
    if (normalized === "openticketprocessingwithsupplier") {
      return "open_with_supplier";
    }
    if (normalized === "manualopenticketprocessing") {
      return "open_processing";
    }
    if (
      normalized === "pending" ||
      normalized === "manualreviewrequired" ||
      normalized === "openticketrequestsubmitted"
    ) {
      return "needs_review";
    }
    return "unknown";
  }

  const status = data?.status;
  const quoted = hasChangeQuote(data);
  const expired = quoted && isChangeQuoteExpired(data);
  const code = normalizeChangeStatus(status);
  const sendFailed =
    isChangeTechnicalSendFailure(data) || looksLikeChangeSendFailure(data);

  if (
    ["Denied", "Rejected", "Cancelled", "Canceled"].includes(status) ||
    isChangeDeclinedByAgentStatus(status) ||
    code === "namechangerequestdenied" ||
    code === "datechangerequestdenied" ||
    code === "datechangerequestcanceled" ||
    code === "datechangerequestcancelled"
  ) {
    return "declined";
  }
  if (isChangeValidationFailedStatus(status)) return "validation_failed";
  if (isChangeFailureSettledStatus(status)) return "failure_settled";
  if (isChangeFailureSettlementPendingStatus(status)) return "failure_settling";
  if (isChangeSettlementFailedStatus(status)) return "settlement_failed";
  if (isChangeSupplierFailedStatus(status)) return "supplier_failed";
  if (
    status === "Success" ||
    status === "Closed" ||
    status === "ChangedOrder" ||
    code === "datechangecompleted" ||
    code === "namechangecompleted"
  ) {
    return "completed";
  }
  if (isChangeRefundDueStatus(status)) return "needs_airline_submit";
  if (isChangeSettlementPendingStatus(status)) return "needs_reissue";
  if (["PendingChange", "PendingReissue", "Pending Reissue"].includes(status)) {
    return "needs_reissue";
  }
  if (isChangeSubmittedToSupplierStatus(status)) return "awaiting_supplier";
  if (isChangeSendFailedStatus(status)) return "send_failed";
  if (
    isAutoDateChangeProcessingStatus(status) ||
    isChangeRequestSubmittedStatus(status)
  ) {
    // Name-change request created usually needs Ops (no auto price path yet)
    if (code === "namechangerequestsubmitted") return "needs_review";
    return "auto_processing";
  }
  if (isAutoDateChangeFailedStatus(status)) return "needs_quote";
  // DateChangeQuoteExpired / NameChangeQuoteExpired are terminal — no re-quote;
  // agent must open a new request. Soft-expired name quotes are also closed.
  if (
    isChangeQuoteExpiredTerminal(status) ||
    ((code === "namechangequoted" || code === "namechangepaymentrequired") &&
      expired)
  ) {
    return "quote_expired_closed";
  }
  if (isChangeQuoteExpiredStatus(status) || expired) return "quote_expired";
  if (
    isChangePaidStatus(status) ||
    status === "Paid" ||
    code === "namechangepaymentauthorised" ||
    code === "namechangepaymentauthorized"
  ) {
    return sendFailed ? "send_failed" : "needs_airline_submit";
  }
  if (code === "manualreviewrequired") {
    // API status ManualReviewRequired always means Ops eligibility review.
    // Do not remap to send/pay from isConfirmed or leftover payment flags —
    // those paths keep their own statuses (PaymentAuthorised, AutoChangeSubmissionFailed).
    return "needs_review";
  }
  if (isManualChangeProcessingStatus(status)) {
    // ManualDateChangeProcessing / ManualNameChangeProcessing = Ops quote work.
    // Never remap from isConfirmed, payment history, or leftover flags.
    return "needs_quote";
  }
  // Doc: DATE_CHANGE_QUOTED → agent accept/decline/expire (not payment yet)
  if (code === "datechangequoted") {
    if (expired) return "quote_expired";
    return "awaiting_agent";
  }
  // Name-change quoted / payment-required → pay
  if (code === "namechangequoted" || code === "namechangepaymentrequired") {
    return "awaiting_payment";
  }
  // Doc: DATE_CHANGE_ACCEPTED → financial check; PAYMENT_REQUIRED → collect
  if (code === "datechangeaccepted" || code === "paymentrequired") {
    if (expired) return "quote_expired";
    if (isZeroPayableChange(data)) return "needs_airline_submit";
    return "awaiting_payment";
  }
  if (isChangeAlreadyAccepted(data)) {
    if (isChangePaymentSecured(data) || isChangePaidStatus(status)) {
      return sendFailed ? "send_failed" : "needs_airline_submit";
    }
    if (expired) return "quote_expired";
    if (!quoted) return "needs_quote";
    if (isZeroPayableChange(data)) return "needs_airline_submit";
    return "awaiting_payment";
  }
  if (isChangeManualReviewStatus(status) || status === "Pending") {
    return "needs_review";
  }
  if (status === "Approved") {
    if (expired) return "quote_expired";
    if (!quoted) return "needs_quote";
    if (isZeroPayableChange(data)) return "needs_airline_submit";
    return "awaiting_payment";
  }
  if (status === "Failed" || code === "failed" || code === "namechangefailed") {
    return "failed";
  }
  return "unknown";
}

export function getOpenTicketReviewTrigger(data) {
  const status = normalizeChangeStatus(data?.status);
  if (status === "openticketvalidationfailed") return "validation_failed";
  if (status === "openticketrequestsubmitted") return "request_submitted";
  if (status === "manualopenticketprocessing") return "ops_checking";
  if (status === "openticketprocessingwithsupplier") return "with_supplier";
  if (
    status === "additionalinformationrequired" ||
    status === "openticketadditionalinformationrequired"
  ) {
    return "need_more_info";
  }
  if (status === "openticketprocessingfailed" || status === "failed") {
    return "processing_failed";
  }
  if (status === "openticketnoteligible") return "not_eligible";
  if (status === "openticketdeclinedbysupplier") return "airline_declined";
  if (status === "openticketsuccessful") return "successful";
  if (
    status === "openticketcompleted" ||
    status === "success" ||
    status === "closed"
  ) {
    return "completed";
  }
  if (status === "manualreviewrequired" || status === "pending") {
    return "needs_review";
  }
  return "needs_review";
}

export function getOpenTicketReviewGuidance(data) {
  const trigger = getOpenTicketReviewTrigger(data);
  const note = changeStatusNote(data);
  const statusDisplay = data?.status || "Unknown";
  const reason = data?.rejectReason || data?.reason || "";

  const shared = {
    trigger,
    note,
    statusDisplay,
    whyTitle: "Why you're here",
    showCanOpen: false,
    showNotEligible: false,
    showProceed: false,
    showOpened: false,
    showAirlineDeclined: false,
    showCouldNotFinish: false,
    showNeedMoreInfo: false,
    showClose: false,
  };

  const copies = {
    validation_failed: {
      title: "This open ticket could not be started",
      why: "A system check failed before Ops started. Intraverse only checks: ticket exists and is issued, belongs to this agent, not voided or fully refunded, not superseded, and no conflicting request. Airline eligibility was not checked. No money moved.",
      steps: [
        "Read the validation reason.",
        "Close this request.",
        "The agent can submit again if the ticket is still eligible.",
      ],
      questions: ["Which system check failed?"],
      showClose: true,
    },
    request_submitted: {
      title: "Open ticket request received",
      why: "System checks passed. Every open ticket goes to Ops — there is no automatic airline route, no quote, and no charge. You must decide if this ticket can be opened.",
      steps: [
        "Open Review.",
        "Check the issued ticket and original flights.",
        "Open the airline or supplier site if you need to confirm fare or coupon rules.",
        "If it can stay open: This can be opened.",
        "If it cannot: Not eligible, with a plain-language reason.",
      ],
      questions: [
        "Is this the right issued ticket?",
        "Can this ticket be opened, or is it not eligible?",
        "There is no quote and no charge.",
      ],
      showCanOpen: true,
      showNotEligible: true,
    },
    needs_review: {
      title: "Ops must review this open ticket",
      why: "Status ManualReviewRequired on an open ticket means a person must look at airline eligibility. Intraverse does not decide coupon status, flown segments, no-show, or open-ticket rules automatically. There is no quote and no charge.",
      steps: [
        "Open Review.",
        "Check the ticket and original flights on the airline site.",
        "If it can stay open: This can be opened.",
        "If it cannot: Not eligible, with a plain-language reason.",
      ],
      questions: [
        "Did you check this on the airline or supplier site?",
        "Can existing flights be cancelled while the ticket stays for later?",
      ],
      showCanOpen: true,
      showNotEligible: true,
    },
    ops_checking: {
      title: "Check with the airline if this can be opened",
      why: "Ops is assessing airline and fare conditions that the platform cannot validate: coupon status, used segments, no-show, check-in, and whether the ticket can stay open. This is not a quote step and not a send failure.",
      steps: [
        "Open the airline or supplier site.",
        "Check if this ticket can remain open.",
        "If yes: Proceed with the airline (cancel current flights, keep ticket OPEN).",
        "If no: Not eligible, with a customer-friendly reason.",
      ],
      questions: [
        "Can this ticket stay open under airline rules?",
        "Has any segment already been used?",
      ],
      showProceed: true,
      showNotEligible: true,
    },
    with_supplier: {
      title: "Finish the open ticket with the airline",
      why: "Ops already said this can proceed. You are now doing the airline work by hand: cancel the current reservation and preserve the unused ticket. Record the real outcome. A timeout is not an airline decline.",
      steps: [
        "Open the airline or supplier site.",
        "Cancel the current flights and keep the ticket OPEN.",
        "If done: Opened.",
        "If the airline said no: Airline declined.",
        "If they asked for more info: Need more information.",
        "If a system problem stopped you: Could not finish — that is not an airline decline.",
      ],
      questions: [
        "Did the airline finish opening the ticket?",
        "Did the airline say no, or did our side fail?",
      ],
      showOpened: true,
      showAirlineDeclined: true,
      showCouldNotFinish: true,
      showNeedMoreInfo: true,
    },
    need_more_info: {
      title: "Airline asked for more information",
      why: "The airline needs more information before they can open this ticket. This is not an airline decline and not a system failure. There is no quote and no charge.",
      steps: [
        "Get what the airline asked for.",
        "Proceed with the airline again.",
        "Opened only if the airline already opened the ticket.",
      ],
      questions: ["Do you have what the airline asked for?"],
      showProceed: true,
      showOpened: true,
    },
    processing_failed: {
      title: "Could not finish — try again",
      why: "Ops could not complete this because of a technical or operational problem (PNR missing, airline system down, unclear response). This is not an airline decline. Keep this request open.",
      steps: [
        "Try again with the airline.",
        "Opened only if the airline already opened the ticket.",
        "Assign a teammate if this sits too long.",
      ],
      questions: [
        "Was this a system problem, or did the airline actually refuse?",
      ],
      showProceed: true,
      showOpened: true,
    },
    not_eligible: {
      title: "Not eligible to open",
      why: reason
        ? `Ops decided this ticket cannot be opened. Reason: ${reason}`
        : "Ops decided this ticket cannot be opened under airline or fare rules. The original ticket stays as it is. This is not a failed send.",
      steps: ["No further action. Keep the reason for the agent."],
      questions: [],
    },
    airline_declined: {
      title: "Airline declined this open ticket",
      why: reason
        ? `Ops took this to the airline and the airline said no. Reason: ${reason}`
        : "Ops took this to the airline and the airline explicitly declined. The original ticket stays as it is. This is not a technical timeout.",
      steps: ["No further action. Keep the airline reason for the agent."],
      questions: [],
    },
    successful: {
      title: "Airline work done — ticket is OPEN",
      why: "The current flights are off. The ticket stays for future travel. Show OPEN, not cancelled. There is no money step and no expiry tracking.",
      steps: ["This should move to completed. No quote. No charge."],
      questions: [],
    },
    completed: {
      title: "Open ticket completed",
      why: "Request finished. Ticket status is OPEN for a later date change. No settlement, no markup, no fee.",
      steps: ["Done. Keep the original itinerary for audit."],
      questions: [],
    },
  };

  return { ...shared, ...(copies[trigger] || copies.needs_review) };
}

function getOpenTicketStatusOverlay(data, { stage }) {
  const code = normalizeChangeStatus(data?.status);
  const reason = data?.rejectReason || data?.reason || "";
  const note = changeStatusNote(data);
  const noteLine = note ? ` ${note}` : "";

  const overlays = {
    openticketrequestsubmitted: {
      statusLabel: "Request received — Ops review",
      title: "Open ticket request received",
      description:
        "System checks passed. Every open ticket goes to Ops. There is no automatic airline route, no quote, and no charge. Review: this can be opened, or not eligible.",
      tone: "action",
    },
    openticketvalidationfailed: {
      statusLabel: "Could not start",
      title: "This open ticket could not be started",
      description: `A system check failed before Ops started.${noteLine} Airline eligibility was not checked. Close this request. No money moved.`,
      tone: "danger",
    },
    manualreviewrequired: {
      statusLabel: "Ops must review eligibility",
      title: "Ops must review this open ticket",
      description:
        "A person must check airline eligibility. Intraverse does not decide coupon status, used segments, or open-ticket rules automatically. There is no quote and no charge.",
      tone: "action",
    },
    pending: {
      statusLabel: "Ops must review eligibility",
      title: "Ops must review this open ticket",
      description:
        "A person must check airline eligibility. There is no quote and no charge.",
      tone: "action",
    },
    manualopenticketprocessing: {
      statusLabel: "Check airline if it can open",
      title: "Check with the airline if this can be opened",
      description:
        "Check fare, coupon, and airline rules. If it can stay open, proceed with the airline by hand. If not, mark not eligible. There is no quote and no charge. A timeout is not an airline decline.",
      tone: "action",
    },
    openticketprocessingwithsupplier: {
      statusLabel: "Working with the airline",
      title: "Finish the open ticket with the airline",
      description:
        "Cancel the current flights and keep the ticket OPEN. Then record: opened, airline declined, need more information, or could not finish. A timeout is not an airline decline. There is no quote and no charge.",
      tone: "action",
    },
    additionalinformationrequired: {
      statusLabel: "Need more information",
      title: "Airline asked for more information",
      description:
        "Get what the airline asked for, then proceed again. This is not an airline decline. There is no quote and no charge.",
      tone: "action",
    },
    openticketadditionalinformationrequired: {
      statusLabel: "Need more information",
      title: "Airline asked for more information",
      description:
        "Get what the airline asked for, then proceed again. This is not an airline decline. There is no quote and no charge.",
      tone: "action",
    },
    openticketprocessingfailed: {
      statusLabel: "Could not finish — try again",
      title: "Could not finish — try again",
      description: `A technical or operational problem stopped this.${noteLine} This is not an airline decline. Try again with the airline, or record opened only if the airline already opened the ticket.`,
      tone: "action",
    },
    failed: {
      statusLabel: "Could not finish — try again",
      title: "Could not finish — try again",
      description:
        "A technical or operational problem stopped this. This is not an airline decline. Try again with the airline.",
      tone: "action",
    },
    openticketnoteligible: {
      statusLabel: "Not eligible",
      title: "Not eligible to open",
      description: reason
        ? `This ticket cannot be opened. Reason: ${reason}`
        : "Ops decided this ticket cannot be opened. The original ticket stays as it is.",
      tone: "danger",
    },
    openticketrequestcanceled: {
      statusLabel: "Cancelled",
      title: "This open ticket was cancelled",
      description:
        "The request was cancelled. The ticket was not opened and the original flights stay as they are. No money moved.",
      tone: "danger",
    },
    openticketrequestcancelled: {
      statusLabel: "Cancelled",
      title: "This open ticket was cancelled",
      description:
        "The request was cancelled. The ticket was not opened and the original flights stay as they are. No money moved.",
      tone: "danger",
    },
    // Ops sometimes closes an open ticket with the name-change cancel status.
    namechangerequestcanceled: {
      statusLabel: "Cancelled",
      title: "This open ticket was cancelled",
      description: reason
        ? `Ops closed this open ticket. Reason: ${reason}`
        : "Ops closed this open ticket. The ticket was not opened and the original flights stay as they are. No money moved.",
      tone: "danger",
    },
    namechangerequestcancelled: {
      statusLabel: "Cancelled",
      title: "This open ticket was cancelled",
      description: reason
        ? `Ops closed this open ticket. Reason: ${reason}`
        : "Ops closed this open ticket. The ticket was not opened and the original flights stay as they are. No money moved.",
      tone: "danger",
    },
    openticketdeclinedbysupplier: {
      statusLabel: "Airline declined open ticket",
      title: "Airline declined this open ticket",
      description: reason
        ? `The airline said no. Reason: ${reason}`
        : "Ops took this to the airline and the airline declined. The original ticket stays as it is. This is not a technical timeout.",
      tone: "danger",
    },
    openticketsuccessful: {
      statusLabel: "Ticket is OPEN",
      title: "Airline work done — ticket is OPEN",
      description:
        "Current flights are off. Ticket stays for future travel. Show OPEN, not cancelled. No money moved. No expiry tracking.",
      tone: "success",
    },
    openticketcompleted: {
      statusLabel: "Completed",
      title: "Open ticket completed",
      description:
        "Request finished. Ticket is OPEN for a later date change. No settlement, no markup, no fee.",
      tone: "success",
    },
    success: {
      statusLabel: "Completed",
      title: "Open ticket completed",
      description:
        "Request finished. Ticket is OPEN for a later date change. No money moved.",
      tone: "success",
    },
    closed: {
      statusLabel: "Completed",
      title: "Open ticket completed",
      description:
        "Request finished. Ticket is OPEN for a later date change. No money moved.",
      tone: "success",
    },
  };

  if (
    code === "denied" ||
    code === "rejected" ||
    code === "cancelled" ||
    code === "canceled"
  ) {
    return {
      statusLabel: "Declined",
      title: "Open ticket declined",
      description: reason
        ? `This open ticket was declined. Reason: ${reason}`
        : "This open ticket was declined. The original ticket stays as it is.",
      tone: "danger",
    };
  }

  return overlays[code] || null;
}

function getChangeStatusOverlay(data, { stage, kind, isDetails }) {
  if (isOpenTicketChange(data)) {
    return getOpenTicketStatusOverlay(data, { stage });
  }
  const code = normalizeChangeStatus(data?.status);
  const note = changeStatusNote(data);
  const noteLine = note ? ` ${note}` : "";

  const overlays = {
    datechangerequestsubmitted: {
      statusLabel: "Request received — get the price",
      title: "The request is in. Get the airline price next",
      description: `Validation passed. The computer should ask the airline for a ${kind} price. If a quote does not appear, take over: check the airline and enter the quote. A timeout is not an airline decline. Do not send anything to the airline yet.`,
      tone: "wait",
    },
    datechangevalidationfailed: {
      statusLabel: "Could not start",
      title: `This ${kind} could not be started`,
      description:
        "A required check failed before anything was sent. No money moved. Close this request. The agent can submit again if the ticket is still eligible.",
    },
    autodatechangeprocessing: {
      statusLabel: "Getting airline price",
      title: "The computer is asking the airline for a price",
      description:
        "Stay on this request. If this sits too long, take over and enter the quote yourself. A timeout is not an airline decline.",
      tone: "wait",
    },
    autodatechangefailed: {
      statusLabel: "Could not get price — check airline",
      title: "The computer could not get a price. Check the airline",
      description:
        "That is a technical problem, not an airline decline. Check the airline for the new itinerary and price, then enter the quote. Enter final agent amounts (markup fare difference only — change fee & penalty at cost). Service fee is from fee config. Do not send the ticket change yet.",
    },
    manualdatechangeprocessing:
      stage === "needs_airline_submit" || stage === "send_failed"
        ? {
            statusLabel: "Send to airline by hand",
            title: "Go to the airline and submit this by hand",
            description: `Automatic send is not available. Submit this ${kind} with the airline yourself, then confirm sent. Use Manual change only if the airline already finished it. Do not create a new quote on this same accepted request.`,
          }
        : stage === "awaiting_payment"
          ? hasPendingChangeBankTransferProof(data)
            ? {
                statusLabel: "Bank transfer pending approval",
                title: "Match the bank transfer before you send this",
                description:
                  "The agent uploaded transfer proof. Confirm or reject it on this page. Do not submit to the airline until the transfer is confirmed.",
                tone: "wait",
              }
            : {
                statusLabel: "Waiting for agent to pay",
                title: "Quote is ready — waiting for the agent to pay",
                description: isDetails
                  ? "You already entered the fee. The agent must pay before you submit the name change to the airline. If they cannot, pay from the agent wallet. Do not submit to the airline yet."
                  : "The agent accepted. Extra money is due before you submit the change to the airline. If they cannot, pay from the agent wallet. Do not submit to the airline yet.",
                tone: "wait",
              }
          : stage === "awaiting_agent"
            ? {
                statusLabel: "Waiting for agent to accept quote",
                title: "Waiting for the agent to accept this quote",
                description:
                  "The quote is with the agent. They must accept it, decline it, or let it expire. Do not send this to the airline yet. Do not take wallet money yet.",
                tone: "wait",
              }
            : stage === "quote_expired"
              ? {
                  statusLabel: "Quote expired — enter a new one",
                  title: "The quote expired",
                  description:
                    "The old quote cannot be used. Enter a new quote with a fresh expiry. Do not send the expired quote to the airline.",
                  tone: "danger",
                }
              : {
                  statusLabel: "Check airline for price",
                  title: "Check the airline for the itinerary and price",
                  description: isDetails
                    ? "This needs a person. Get the name-change fee on the airline site, then enter the quote. Service fee is from fee config. The agent sees nothing until you send a quote."
                    : "This needs a person. Get the new itinerary and price from the airline, then enter the quote. Enter final agent amounts (markup fare difference only — change fee & penalty at cost). Service fee is from fee config. The agent sees nothing until you send a quote.",
                },
    datechangequoted:
      stage === "quote_expired"
        ? {
            statusLabel: "Quote expired — enter a new one",
            title: "The quote expired",
            description:
              "The old quote cannot be used. Enter a new quote with a fresh expiry. Do not send the expired quote to the airline.",
            tone: "danger",
          }
        : {
            statusLabel: "Waiting for agent to accept quote",
            title: "Waiting for the agent to accept this quote",
            description:
              "The quote is with the agent. They must accept it, decline it, or let it expire. Do not send this to the airline yet. If they accept and extra money is due, payment comes next. You can edit the quote if it is wrong.",
            tone: "wait",
          },
    datechangeaccepted:
      stage === "quote_expired"
        ? {
            statusLabel: "Quote expired — enter a new one",
            title: "The quote expired",
            description:
              "The old quote cannot be used. Enter a new quote with a fresh expiry. Do not send the expired quote to the airline.",
            tone: "danger",
          }
        : {
            statusLabel:
              stage === "needs_airline_submit"
                ? "Accepted — send to airline"
                : "Accepted — check payment",
            title:
              stage === "needs_airline_submit"
                ? "The agent accepted. Send this to the airline"
                : "The agent accepted. Check payment next",
            description:
              stage === "needs_airline_submit"
                ? "Payment is not needed, or it is already secured. Submit this to the airline. Do not create a new quote on this same request."
                : "Take payment from the agent wallet if they cannot pay. Do not send this to the airline until payment is secured.",
          },
    datechangedeclinedbyagent: {
      statusLabel: "Agent declined quote",
      title: "The agent declined this quote",
      description: `No airline change was sent. No money moved. The original ticket stays as it is.`,
      tone: "danger",
    },
    datechangerequestdenied: {
      statusLabel: "Declined by Ops",
      title: "Ops declined this request",
      description:
        "Staff declined this date change. No airline change was sent. The original ticket stays as it is.",
      tone: "danger",
    },
    datechangerequestcanceled: {
      statusLabel: "Agent withdrew",
      title: "The agent withdrew this request",
      description:
        "The agent canceled / withdrew this date change. No airline change was sent. The original ticket stays as it is.",
      tone: "danger",
    },
    datechangerequestcancelled: {
      statusLabel: "Agent withdrew",
      title: "The agent withdrew this request",
      description:
        "The agent canceled / withdrew this date change. No airline change was sent. The original ticket stays as it is.",
      tone: "danger",
    },
    datechangequoteexpired: {
      statusLabel: "Quote expired",
      title: "The quote expired",
      description:
        "The payment / accept window closed. This request is closed — you cannot enter a new quote here. The original ticket stays as it is. The agent must submit a new date change if they still need it.",
      tone: "danger",
    },
    paymentrequired:
      stage === "quote_expired"
        ? {
            statusLabel: "Quote expired — enter a new one",
            title: "The quote expired",
            description:
              "The payment window closed. Enter a new quote with a fresh expiry. Do not send the expired quote to the airline.",
            tone: "danger",
          }
        : {
            statusLabel: hasPendingChangeBankTransferProof(data)
              ? "Bank transfer pending approval"
              : "Agent must pay",
            title: hasPendingChangeBankTransferProof(data)
              ? "Match the bank transfer before you send this"
              : "Extra money is due before you send this",
            description: hasPendingChangeBankTransferProof(data)
              ? "The agent uploaded transfer proof. Confirm or reject it on this page. Do not submit to the airline until the transfer is confirmed."
              : "Do not submit this to the airline until the wallet has enough money. Ask the agent to fund it, or pay from the agent wallet here.",
          },
    paymentauthorised: {
      statusLabel: "Paid — send to airline",
      title: "Payment is held. Send this to the airline",
      description:
        "The extra amount is reserved. Do not take money again. Confirm sent to airline after you send it. A timeout is not an airline decline.",
    },
    paymentauthorized: {
      statusLabel: "Paid — send to airline",
      title: "Payment is held. Send this to the airline",
      description:
        "The extra amount is reserved. Do not take money again. Confirm sent to airline after you send it. A timeout is not an airline decline.",
    },
    paymentnotrequired: {
      statusLabel: "No extra pay — send to airline",
      title: "Nothing extra is due. Send this to the airline",
      description:
        "There is no extra collection. Submit this to the airline now. Do not take wallet money.",
    },
    refunddue: {
      statusLabel: "Send to airline first",
      title: "Credit is waiting. Send this to the airline first",
      description:
        "Do not credit the agent wallet yet. The airline must complete the change first. Confirm sent to airline next.",
    },
    datechangesubmitting: {
      statusLabel: "Sending to airline",
      title: "This is being sent to the airline",
      description:
        "If this sits, confirm sent or submit it by hand. A timeout is not an airline decline. Do not create a new quote on this same request.",
    },
    autochangesubmission: {
      statusLabel: "Sending to airline",
      title: "The computer is sending this to the airline",
      description:
        "Wait. If this sits too long, submit it by hand. A timeout is not an airline decline. Do not create a new quote on this same request.",
      tone: "wait",
    },
    autochangesubmissionfailed: {
      statusLabel: "Send failed — do it by hand",
      title: "The computer could not send this. Do it by hand",
      description: `That is a technical problem, not an airline decline.${noteLine} Go to the airline and submit this ${kind} yourself, then confirm sent. Use Manual change only if the airline already finished it. Do not create a new quote on this same request.`,
    },
    submittedtosupplier: {
      statusLabel: "Waiting on airline",
      title: "The airline has this change",
      description:
        "Wait for the airline answer. Do not create a new quote on this same request. Use Manual change only if you have confirmed the airline already finished it. A timeout is not an airline decline.",
      tone: "wait",
    },
    supplierprocessing: {
      statusLabel: "Waiting on airline",
      title: "The airline is still working on this",
      description:
        "Wait. Do not create a new quote on this same request. Use Manual change only if you have confirmed the airline already finished it. A timeout is not an airline decline.",
      tone: "wait",
    },
    datechangesuccessful: {
      statusLabel: "Airline done — write the new ticket",
      title: "The airline finished. Write the new ticket into Intraverse",
      description:
        "Confirm re-issue only if a new ticket has already been issued. That writes the new dates or names into Intraverse. Keep the original itinerary for audit. Money settlement is a separate step. Do not treat a wallet problem as an un-change.",
    },
    datechangedeclinedbysupplier: {
      statusLabel: "Airline said no",
      title: "The airline declined this change",
      description: `This is an airline no, not a timeout.${noteLine} Close this request. The original ticket stays as it is. Do not create a new quote on this same request. Use Manual change only if you later confirm the airline did change it.`,
    },
    datechangefailedbysupplier: {
      statusLabel: "Airline could not change it",
      title: "The airline could not complete this change",
      description: `This is an airline failure, not a timeout.${noteLine} Close this request. The original ticket stays as it is. Do not create a new quote on this same request.`,
    },
    settlementpending: {
      statusLabel: "Finish the ticket in Intraverse",
      title: "Airline change is done. Finish it in Intraverse",
      description:
        "Confirm re-issue if the new ticket is ready. Money is settling separately. A wallet problem does not un-change the ticket.",
    },
    additionalcollectiondebited: {
      statusLabel: "Extra money taken — write the ticket",
      title: "The extra amount has been taken",
      description:
        "Confirm re-issue if the new ticket is not in Intraverse yet. Then this should complete.",
    },
    nofinancialadjustment: {
      statusLabel: "No money to move — write the ticket",
      title: "Nothing to collect or credit",
      description:
        "Confirm re-issue if the new ticket is not in Intraverse yet. Then this should complete.",
    },
    balancerefunded: {
      statusLabel: "Credit sent — write the ticket",
      title: "The credit has been sent",
      description:
        "Confirm re-issue if the new ticket is not in Intraverse yet. Then this should complete.",
    },
    settlementfailed: {
      statusLabel: "Airline done — money failed",
      title: "The airline change stays. The wallet step failed",
      description:
        "Do not change a successful change back to failed. Confirm re-issue if the new ticket is ready, and assign this so finance can fix the money.",
    },
    settlementreversalpending: {
      statusLabel: "Releasing payment",
      title: "The airline did not change it. Money is being released",
      description:
        "Keep this as a failed change. Assign a teammate if this sits too long.",
      tone: "wait",
    },
    paymentreservationreleased: {
      statusLabel: "Payment released",
      title: "The reserved payment has been released",
      description:
        "The airline did not complete this change. Keep this as a failed change until it is marked settled.",
      tone: "wait",
    },
    paymentreversed: {
      statusLabel: "Payment reversed",
      title: "The debit has been reversed",
      description:
        "The airline did not complete this change. Keep this as a failed change until it is marked settled.",
      tone: "wait",
    },
    datechangecompleted: {
      statusLabel: "Completed",
      title: `${kind.charAt(0).toUpperCase()}${kind.slice(1)} completed`,
      description:
        "The new ticket is in Intraverse. The original itinerary is kept for audit. No further action is needed.",
    },
    datechangefailuresettled: {
      statusLabel: "Failed — money settled",
      title: "This change failed and the money is settled",
      description:
        "The airline did not complete this change. Any reserved payment has been cleaned up. Keep this as failed, not completed.",
    },
    namechangerequestsubmitted: {
      statusLabel: "Review this request",
      title: `Check this ${kind} on the airline`,
      description:
        "The agent submitted a details change. Open Review. Check the airline site. If the change is allowed, Accept — then enter a quote. If not, Decline. Do not submit to the airline yet. Do not invent amounts.",
    },
    namechangequoted:
      stage === "quote_expired_closed"
        ? {
            statusLabel: "Quote expired",
            title: "The quote expired",
            description:
              "The payment window closed. This request is closed — you cannot enter a new quote here. The original ticket stays as it is. The agent must submit a new details change if they still need it.",
            tone: "danger",
          }
        : {
            // Same as date PaymentRequired: pending proof overrides "waiting to pay".
            statusLabel: hasPendingChangeBankTransferProof(data)
              ? "Bank transfer pending approval"
              : "Waiting for agent to pay",
            title: hasPendingChangeBankTransferProof(data)
              ? "Match the bank transfer before you send this"
              : "Quote is ready — waiting for the agent to pay",
            description: hasPendingChangeBankTransferProof(data)
              ? "The agent uploaded transfer proof. Confirm or reject it on this page. Do not submit to the airline until the transfer is confirmed."
              : "You already entered the fee. The agent must pay before you submit the name change to the airline. If they cannot, pay from the agent wallet. Do not submit to the airline yet.",
            tone: "wait",
          },
    namechangepaymentrequired:
      stage === "quote_expired_closed"
        ? {
            statusLabel: "Quote expired",
            title: "The quote expired",
            description:
              "The payment window closed. This request is closed — you cannot enter a new quote here. The original ticket stays as it is. The agent must submit a new details change if they still need it.",
            tone: "danger",
          }
        : {
            statusLabel: hasPendingChangeBankTransferProof(data)
              ? "Bank transfer pending approval"
              : "Agent must pay",
            title: hasPendingChangeBankTransferProof(data)
              ? "Match the bank transfer before you send this"
              : "Extra money is due before you send this",
            description: hasPendingChangeBankTransferProof(data)
              ? "The agent uploaded transfer proof. Confirm or reject it on this page. Do not submit to the airline until the transfer is confirmed."
              : "Do not submit this to the airline until the wallet has enough money. Ask the agent to fund it, or pay from the agent wallet here.",
          },
    namechangepaymentauthorised: {
      statusLabel: "Paid — send to airline",
      title: "Payment is held. Send this to the airline",
      description:
        "The extra amount is reserved. Do not take money again. Confirm sent to airline after you send it. A timeout is not an airline decline.",
    },
    namechangepaymentauthorized: {
      statusLabel: "Paid — send to airline",
      title: "Payment is held. Send this to the airline",
      description:
        "The extra amount is reserved. Do not take money again. Confirm sent to airline after you send it. A timeout is not an airline decline.",
    },
    namechangesubmittedtosupplier: {
      statusLabel: "Waiting on airline",
      title: "The airline has this change",
      description:
        "Wait for the airline answer. Do not create a new quote on this same request. Use Manual change only if you have confirmed the airline already finished it. A timeout is not an airline decline.",
      tone: "wait",
    },
    namechangecompleted: {
      statusLabel: "Completed",
      title: `${kind.charAt(0).toUpperCase()}${kind.slice(1)} completed`,
      description:
        "The new ticket is in Intraverse. The original itinerary is kept for audit. No further action is needed.",
    },
    namechangefailed: {
      statusLabel: "Failed — check next step",
      title: `This ${kind} failed`,
      description: `Processing failed.${noteLine} Check the airline site for the real outcome. Do not invent a success. Close if the ticket stays unchanged, or use Manual change only if the airline already finished the change.`,
    },
    namechangerequestdenied: {
      statusLabel: "Declined by Ops",
      title: `This ${kind} was declined`,
      description:
        "Ops declined the request. No airline change was sent. The original ticket stays as it is.",
      tone: "danger",
    },
    namechangerequestcanceled: {
      statusLabel: "Agent withdrew",
      title: "The agent canceled this request",
      description:
        "The agent canceled. No airline change was sent. The original ticket stays as it is.",
      tone: "danger",
    },
    namechangerequestcancelled: {
      statusLabel: "Agent withdrew",
      title: "The agent canceled this request",
      description:
        "The agent canceled. No airline change was sent. The original ticket stays as it is.",
      tone: "danger",
    },
    namechangequoteexpired: {
      statusLabel: "Quote expired",
      title: "The quote expired",
      description:
        "This request is closed. You cannot enter a new quote on it. The original ticket stays as it is. The agent must submit a new details change if they still need it.",
      tone: "danger",
    },
    paid: {
      statusLabel: "Paid — send to airline",
      title: "Payment is done. Send this to the airline",
      description:
        "Do not take money again. Confirm sent to airline after you send it. A timeout is not an airline decline.",
    },
    approved: {
      statusLabel:
        stage === "needs_quote"
          ? "Check airline for price"
          : "Waiting for agent to accept quote",
    },
  };

  // Same ops path as ManualDateChangeProcessing (quote vs send by stage).
  overlays.manualnamechangeprocessing = overlays.manualdatechangeprocessing;

  if (code === "manualreviewrequired") {
    const autoFailed = isManualReviewAfterAutoDateChangeFailed(data);
    const failureReason = getChangeFailureReason(data);
    const failureLine = failureReason ? ` Failure reason: ${failureReason}.` : "";
    return {
      statusLabel: "Review this request",
      title: autoFailed
        ? `Auto ${kind} pricing failed — review this request`
        : `Check this ${kind} on the airline`,
      description: autoFailed
        ? `Automatic pricing failed (AutoDateChangeFailed), so Ops must review this request.${failureLine} Check the airline site. If the change is allowed, Accept — then enter a quote. If not, Decline. This is not a failed send and not an airline decline.`
        : `Status ${data?.status || "ManualReviewRequired"}: the platform could not auto-confirm this request is valid, or this supplier does not support automatic ${kind} pricing. Check the airline site. If the change is allowed, Accept — then enter a quote. If not, Decline. This is not a failed send and not an airline decline.`,
      tone: "action",
    };
  }

  return overlays[code] || null;
}

export function getChangeStatusLabelForCode(status) {
  const openLabel = getOpenTicketStatusOverlay(
    { status, changeRequest: "Open" },
    { stage: "" },
  );
  if (
    openLabel?.statusLabel &&
    normalizeChangeStatus(status).startsWith("openticket")
  ) {
    return openLabel.statusLabel;
  }
  if (
    [
      "additionalinformationrequired",
      "manualopenticketprocessing",
      "manualreviewrequired",
      "pending",
    ].includes(normalizeChangeStatus(status))
  ) {
    // Ambiguous without changeRequest — fall through to date-change overlay first.
  }
  const code = normalizeChangeStatus(status);
  const kind = code.startsWith("namechange") ? "details change" : "date change";
  const overlays = getChangeStatusOverlay({ status }, { stage: "", kind });
  if (overlays?.statusLabel) return overlays.statusLabel;
  const fallback = {
    pending: "Review this request",
    manualreviewrequired: "Review this request",
    // Request
    datechangerequestsubmitted: "Request received — get the price",
    datechangevalidationfailed: "Could not start",
    // Processing
    autodatechangeprocessing: "Getting airline price",
    autodatechangefailed: "Could not get price — check airline",
    manualdatechangeprocessing: "Check airline for price",
    // Quote
    datechangequoted: "Waiting for agent to accept quote",
    datechangeaccepted: "Accepted — check payment",
    datechangedeclinedbyagent: "Agent declined quote",
    datechangerequestdenied: "Declined by Ops",
    datechangerequestcanceled: "Agent withdrew",
    datechangerequestcancelled: "Agent withdrew",
    datechangequoteexpired: "Quote expired",
    // Financial
    paymentrequired: "Agent must pay",
    paymentauthorised: "Paid — send to airline",
    paymentauthorized: "Paid — send to airline",
    paymentnotrequired: "No extra pay — send to airline",
    refunddue: "Send to airline first",
    // Supplier submission
    datechangesubmitting: "Sending to airline",
    autochangesubmission: "Sending to airline",
    autochangesubmissionfailed: "Send failed — do it by hand",
    submittedtosupplier: "Waiting on airline",
    supplierprocessing: "Waiting on airline",
    // Supplier outcome
    datechangesuccessful: "Airline done — write the new ticket",
    datechangedeclinedbysupplier: "Airline said no",
    datechangefailedbysupplier: "Airline could not change it",
    // Settlement
    settlementpending: "Finish the ticket in Intraverse",
    additionalcollectiondebited: "Extra money taken — write the ticket",
    nofinancialadjustment: "No money to move — write the ticket",
    balancerefunded: "Credit sent — write the ticket",
    settlementfailed: "Airline done — money failed",
    settlementreversalpending: "Releasing payment",
    paymentreservationreleased: "Payment released",
    paymentreversed: "Payment reversed",
    // Final
    datechangecompleted: "Completed",
    datechangefailuresettled: "Failed — money settled",
    pendingchange: "Airline done — write the new ticket",
    pendingreissue: "Airline done — write the new ticket",
    failed: "Failed — check next step",
    namechangefailed: "Failed — check next step",
    denied: "Declined",
    rejected: "Declined",
    namechangerequestdenied: "Declined by Ops",
    namechangerequestcanceled: "Agent withdrew",
    namechangerequestcancelled: "Agent withdrew",
    namechangerequestsubmitted: "Review this request",
    manualnamechangeprocessing: "Check airline for price",
    namechangequoted: "Waiting for agent to pay",
    namechangepaymentrequired: "Agent must pay",
    namechangepaymentauthorised: "Paid — send to airline",
    namechangepaymentauthorized: "Paid — send to airline",
    namechangesubmittedtosupplier: "Waiting on airline",
    namechangecompleted: "Completed",
    namechangequoteexpired: "Quote expired",
    success: "Completed",
    closed: "Completed",
    changedorder: "Completed",
    additionalinformationrequired: "Need more information",
    manualopenticketprocessing: "Check airline if it can open",
    openticketprocessingwithsupplier: "Working with the airline",
    openticketrequestsubmitted: "Request received — Ops review",
    openticketvalidationfailed: "Could not start",
    openticketnoteligible: "Not eligible",
    openticketrequestcanceled: "Cancelled",
    openticketrequestcancelled: "Cancelled",
    openticketdeclinedbysupplier: "Airline declined open ticket",
    openticketprocessingfailed: "Could not finish — try again",
    openticketsuccessful: "Ticket is OPEN",
    openticketcompleted: "Completed",
  };
  return fallback[code] || null;
}

export function getChangeAdminGuidance(data) {
  const stage = getChangeAdminStage(data);
  const kind = getChangeKindLabel(data);
  const isDetails = isDetailsChange(data);
  const reason = data?.rejectReason || data?.reason || "";

  const guidance = {
    needs_review: (() => {
      const autoFailed = isManualReviewAfterAutoDateChangeFailed(data);
      const failureReason = getChangeFailureReason(data);
      const failureLine = failureReason ? ` Failure reason: ${failureReason}.` : "";
      return {
        title: isOpenTicketChange(data)
          ? `Check this ${kind} on the airline`
          : autoFailed
            ? `Auto ${kind} pricing failed — review this request`
            : `Check this ${kind} on the airline`,
        description: isOpenTicketChange(data)
          ? "System checks passed or Ops must review eligibility. Open Review, check the ticket and original flights on the airline site, then: this can be opened, or not eligible. There is no quote and no charge."
          : autoFailed
            ? `Automatic pricing failed (AutoDateChangeFailed), so Ops must review this request.${failureLine} Open Review. Check the airline site and fare rules. If the change is allowed, Accept — then enter a quote. If not, Decline. Accepting is not a quote. This is not a failed send and not an airline decline.`
            : `Status ${data?.status || "ManualReviewRequired"}: the platform could not auto-confirm this request is valid, or this supplier does not support automatic ${kind} pricing. Open Review. Check the airline site and fare rules. If the change is allowed, Accept — then enter a quote. If not, Decline. Accepting is not a quote. This is not a failed send and not an airline decline.`,
        statusLabel: isOpenTicketChange(data)
          ? "Ops must review eligibility"
          : "Review this request",
        tone: "action",
        primary: [
          { key: CHANGE_ACTION.review, label: "Review this request" },
          ...(canRetryAutoChange(data)
            ? [
                {
                  key: CHANGE_ACTION.retryAuto,
                  label: getChangeRetryAutoActionLabel(),
                },
              ]
            : []),
          { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
        ],
        secondary: isOpenTicketChange(data)
          ? []
          : [{ key: CHANGE_ACTION.decline, label: "Decline request" }],
      };
    })(),
    open_processing: {
      title: "Check with the airline",
      description:
        "You are checking fare, coupon, and airline rules. If this ticket can stay open, proceed with the airline by hand: cancel the current flights and keep the ticket OPEN. If it cannot, mark it not eligible and write a plain-language reason. There is no quote and no charge. A timeout is not an airline decline.",
      statusLabel: "Check airline if it can open",
      tone: "action",
      primary: [
        { key: CHANGE_ACTION.proceedOpen, label: "Proceed with the airline" },
        { key: CHANGE_ACTION.notEligible, label: "Not eligible" },
      ],
      secondary: [{ key: CHANGE_ACTION.assign, label: "Assign to teammate" }],
    },
    open_with_supplier: {
      title: "Finish this with the airline",
      description:
        "Cancel the current flights and keep the ticket OPEN. Then record the result: opened, airline said no, or could not finish. A timeout is not an airline decline. There is no quote and no charge.",
      statusLabel: "Working with the airline",
      tone: "action",
      primary: [{ key: CHANGE_ACTION.recordOpened, label: "Opened" }],
      secondary: [
        { key: CHANGE_ACTION.airlineDeclined, label: "Airline declined" },
        { key: CHANGE_ACTION.processingFailed, label: "Could not finish" },
        { key: CHANGE_ACTION.needMoreInfo, label: "Need more information" },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
      ],
    },
    open_not_eligible: {
      title: "Not eligible",
      description: reason
        ? `This ticket cannot be opened. Reason: ${reason}`
        : "This ticket cannot be opened. The original ticket stays as it is.",
      statusLabel: "Not eligible",
      tone: "danger",
      primary: [],
      secondary: [],
    },
    open_completed: {
      title: "Open ticket completed",
      description:
        "The current flights are off. The ticket is OPEN for a later date change. Do not show it as cancelled. No money moved.",
      statusLabel: "Completed",
      tone: "success",
      primary: [],
      secondary: [],
    },
    open_failed: {
      title: "Could not finish — try again",
      description:
        "This is a technical or operational problem, not an airline decline. Stay on this request and try again, or record opened only if the airline already opened the ticket.",
      statusLabel: "Could not finish — try again",
      tone: "action",
      primary: [
        { key: CHANGE_ACTION.proceedOpen, label: "Try again with the airline" },
        { key: CHANGE_ACTION.recordOpened, label: "Opened" },
      ],
      secondary: [{ key: CHANGE_ACTION.assign, label: "Assign to teammate" }],
    },
    open_needs_info: {
      title: "Need more information",
      description:
        "The airline asked for more information. Get what they need, then proceed again. This is not an airline decline. There is no quote and no charge.",
      statusLabel: "Need more information",
      tone: "action",
      primary: [
        { key: CHANGE_ACTION.proceedOpen, label: "Proceed with the airline" },
      ],
      secondary: [
        { key: CHANGE_ACTION.recordOpened, label: "Opened" },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
      ],
    },
    open_validation_failed: {
      title: "This open ticket could not be started",
      description:
        "Validation failed before Ops started work. No money moved. The original ticket stays as it is. Close this request, then the agent can submit again if it is still eligible.",
      statusLabel: "Could not start",
      tone: "danger",
      primary: [{ key: CHANGE_ACTION.decline, label: "Close this request" }],
      secondary: [{ key: CHANGE_ACTION.assign, label: "Assign to teammate" }],
    },
    auto_processing: {
      title: "Getting the change price",
      description:
        "The computer is asking the airline for a change price. Stay on this request. If this sits here too long, take over: check the airline yourself, then enter the quote. A timeout is not an airline decline.",
      statusLabel: "Getting airline price",
      tone: "wait",
      primary: [
        {
          key: CHANGE_ACTION.quote,
          label: "Take over — enter quote from airline",
        },
      ],
      secondary: [{ key: CHANGE_ACTION.assign, label: "Assign to teammate" }],
    },
    needs_quote: {
      title: "Check the airline for the price, then enter the quote",
      description: isDetails
        ? "There is no automatic price for this details change. Open Check airline for price for the steps. On the airline site, get the name-change fee. Then Enter quote from airline. Do not invent amounts. Do not submit to the airline yet."
        : "The computer could not get a supplier price, or Ops must price this by hand. Open Check airline for price for the steps. Get the itinerary and price on the airline site. Then Enter quote from airline. A timeout is not an airline decline. Do not submit to the airline yet.",
      statusLabel: "Check airline for price",
      tone: "action",
      primary: [
        { key: CHANGE_ACTION.review, label: "Check airline for price" },
        { key: CHANGE_ACTION.quote, label: "Enter quote from airline" },
      ],
      secondary: [
        { key: CHANGE_ACTION.assign, label: "Reassign" },
        { key: CHANGE_ACTION.decline, label: "Decline request" },
      ],
    },
    awaiting_agent: {
      title: "Waiting for the agent to accept this quote",
      description:
        "The quote is with the agent. They must accept it, decline it, or let it expire. Do not send this to the airline yet. Do not take wallet money yet. You can edit the quote if the numbers are wrong.",
      statusLabel: "Waiting for agent to accept quote",
      tone: "wait",
      primary: [],
      secondary: [
        { key: CHANGE_ACTION.quote, label: getChangeQuoteActionLabel(data) },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
        { key: CHANGE_ACTION.decline, label: "Decline request" },
      ],
    },
    awaiting_payment: hasPendingChangeBankTransferProof(data)
      ? {
          title: "Bank transfer awaiting your approval",
          description:
            "The agent paid by bank transfer and uploaded proof. Match the receipt, then confirm or reject. Do not send to the airline until you confirm. Wallet pay is not needed while a proof is pending.",
          statusLabel: "Bank transfer pending approval",
          tone: "wait",
          primary: [
            {
              key: CHANGE_ACTION.reviewBankProof,
              label: "Review bank payment proof",
            },
          ],
          secondary: [{ key: CHANGE_ACTION.decline, label: "Decline request" }],
        }
      : {
          title: "Waiting for the agent to pay",
          description: isDetails
            ? "You already entered the fee. The agent must pay before you submit the name change to the airline. If they cannot, pay from the agent wallet. Do not submit to the airline yet."
            : "The agent accepted the quote. Extra money is due before you submit to the airline. If they cannot pay, take payment from the agent wallet. Do not treat a wait here as a decline.",
          statusLabel: "Waiting for agent to pay",
          tone: "wait",
          primary: [
            { key: CHANGE_ACTION.adminPay, label: "Pay from agent wallet" },
          ],
          secondary: [
            {
              key: CHANGE_ACTION.quote,
              label: getChangeQuoteActionLabel(data),
            },
            { key: CHANGE_ACTION.decline, label: "Decline request" },
          ],
        },
    quote_expired: {
      title: "Quote expired",
      description:
        "The agent did not accept or pay in time. Enter a new quote with a fresh expiry. Do not automatically reprice an already accepted change — this is a new quote for this request. There is no REPRICE_REQUIRED state.",
      statusLabel: "Quote expired — enter a new one",
      tone: "danger",
      primary: [{ key: CHANGE_ACTION.quote, label: "Enter new quote" }],
      secondary: [
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
        { key: CHANGE_ACTION.decline, label: "Decline request" },
      ],
    },
    quote_expired_closed: {
      title: "Quote expired",
      description: `The agent did not accept or pay in time. This ${kind} request is closed — you cannot enter a new quote here. The original ticket stays as it is. The agent must submit a new request if they still need the change.`,
      statusLabel: "Quote expired",
      tone: "danger",
      primary: [],
      secondary: [],
    },
    awaiting_supplier: {
      title: "Waiting on the airline",
      description: `The ${kind} has been sent. Wait for the airline answer. Do not create a new quote on this same request. If the airline already finished it and this is stuck, use Manual change from the menu. A timeout is not an airline decline. If the airline said no, Decline and choose airline / supplier. If Ops is stopping the request, Decline and choose team.`,
      statusLabel: "Waiting on airline",
      tone: "wait",
      primary: [],
      secondary: [
        { key: CHANGE_ACTION.decline, label: "Decline request" },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
      ],
    },
    needs_airline_submit: {
      title: `Send this ${kind} to the airline`,
      description: isZeroPayableChange(data)
        ? `No extra money is due. Your only job now is to submit this ${kind} to the airline or supplier. Click Confirm sent to airline after you have sent it. A timeout is not an airline decline — try again. Use Manual change only if you already finished it with the airline yourself.`
        : `The agent has already paid. Do not take money again. Your only job now is to submit this ${kind} to the airline or supplier. Click Confirm sent to airline after you have sent it. A timeout is not an airline decline — try again. Use Manual change only if you already finished it with the airline yourself.`,
      statusLabel: "Submit to airline",
      tone: "action",
      primary: [
        { key: CHANGE_ACTION.submit, label: "Confirm sent to airline" },
      ],
      secondary: [
        {
          key: CHANGE_ACTION.manualChange,
          label: "Record successful exchange",
        },
        { key: CHANGE_ACTION.airlineDeclined, label: "Airline declined" },
        {
          key: CHANGE_ACTION.processingFailed,
          label: "Supplier / tech failure",
        },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
      ],
    },
    send_failed: {
      title: "Sending failed — try again",
      description: data?.manualInterventionReason
        ? `${data.manualInterventionReason}. That is a technical problem, not an airline decline. Confirm sent to airline again. Use Manual change only if you already finished it with the airline yourself. Do not create a new quote on this same request.`
        : `The computer could not send this ${kind}. That is a technical problem, not an airline decline. Confirm sent to airline again. Use Manual change only if you already finished it with the airline yourself. Do not create a new quote on this same request.`,
      statusLabel: "Send failed — try again",
      tone: "action",
      primary: [
        { key: CHANGE_ACTION.submit, label: "Confirm sent to airline" },
        {
          key: CHANGE_ACTION.manualChange,
          label: "Record successful exchange",
        },
      ],
      secondary: [
        {
          key: CHANGE_ACTION.processingFailed,
          label: "Record tech / supplier failure",
        },
        { key: CHANGE_ACTION.airlineDeclined, label: "Airline declined" },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
      ],
    },
    needs_reissue: {
      title: "Write the new ticket into Intraverse",
      description:
        "The airline has this change. Confirm re-issue only if a new ticket has already been issued. Enter supplier confirmation, new ticket number, and exchange reference when you have them. The original ticket is kept for audit. Do not click this if the airline is still working. This does not take more money.",
      statusLabel: "Confirm re-issue",
      tone: "action",
      primary: [
        { key: CHANGE_ACTION.confirmReissue, label: "Confirm re-issue" },
        {
          key: CHANGE_ACTION.manualChange,
          label: "Record successful exchange",
        },
      ],
      secondary: [
        { key: CHANGE_ACTION.gdsReissue, label: "GDS re-issue" },
        { key: CHANGE_ACTION.airlineDeclined, label: "Airline declined" },
        {
          key: CHANGE_ACTION.processingFailed,
          label: "Supplier / tech failure",
        },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
      ],
    },
    completed: {
      title: `${isDetails ? "Details change" : "Date change"} completed`,
      description:
        "The ticket has been reissued. Keep the original itinerary for audit. No further action is needed.",
      statusLabel: "Completed",
      tone: "success",
      primary: [],
      secondary: [],
    },
    declined: {
      title: isOpenTicketChange(data)
        ? "Airline declined this open ticket"
        : isChangeWithdrawnByAgentStatus(data?.status)
          ? "Agent withdrew this request"
          : isChangeDeclinedByAdminStatus(data?.status)
            ? "Ops declined this request"
            : isChangeDeclinedByAgentStatus(data?.status)
              ? "Agent declined this quote"
              : "Request declined",
      description: reason
        ? `This ${kind} was closed. Reason: ${reason}`
        : isOpenTicketChange(data)
          ? "The airline declined this open ticket. The original ticket stays as it is. This is not a technical timeout."
          : isChangeWithdrawnByAgentStatus(data?.status)
            ? `The agent withdrew this ${kind}. No airline change was sent. The original ticket stays as it is.`
            : isChangeDeclinedByAdminStatus(data?.status)
              ? `Ops declined this ${kind}. No airline change was sent. The original ticket stays as it is.`
              : isChangeDeclinedByAgentStatus(data?.status)
                ? `The agent declined this ${kind} quote. No airline change was sent. The original ticket stays as it is.`
                : `This ${kind} was declined. The original ticket stays as it is.`,
      statusLabel: isOpenTicketChange(data)
        ? "Airline declined open ticket"
        : isChangeWithdrawnByAgentStatus(data?.status)
          ? "Agent withdrew"
          : isChangeDeclinedByAdminStatus(data?.status)
            ? "Declined by Ops"
            : isChangeDeclinedByAgentStatus(data?.status)
              ? "Agent declined quote"
              : "Declined",
      tone: "danger",
      primary: [],
      secondary: [],
    },
    failed: {
      title: "Change failed",
      description: `A technical or supplier error occurred. Do not treat a timeout as an airline decline. Retry the re-issue or assign this to a teammate. If the airline already declined, record airline decline — do not mint a new quote on this same accepted request.`,
      statusLabel: "Failed — check next step",
      tone: "danger",
      primary: [
        {
          key: CHANGE_ACTION.confirmReissue,
          label: "Retry / confirm re-issue",
        },
        {
          key: CHANGE_ACTION.manualChange,
          label: "Record successful exchange",
        },
      ],
      secondary: [
        { key: CHANGE_ACTION.airlineDeclined, label: "Airline declined" },
        {
          key: CHANGE_ACTION.processingFailed,
          label: "Supplier / tech failure",
        },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
        { key: CHANGE_ACTION.decline, label: "Close this request" },
      ],
    },
    supplier_failed: {
      title: "The airline did not make this change",
      description: `The airline declined or failed this ${kind}. This is not a technical timeout. Close this request if the original ticket stays as it is. Use Record successful exchange only if you later confirm the airline did change it. Do not create a new quote on this same request.`,
      statusLabel: "Airline declined change",
      tone: "danger",
      primary: [{ key: CHANGE_ACTION.decline, label: "Close this request" }],
      secondary: [
        { key: CHANGE_ACTION.airlineDeclined, label: "Record airline decline" },
        {
          key: CHANGE_ACTION.processingFailed,
          label: "Record supplier failure",
        },
        {
          key: CHANGE_ACTION.manualChange,
          label: "Record successful exchange",
        },
        { key: CHANGE_ACTION.assign, label: "Assign to teammate" },
      ],
    },
    validation_failed: {
      title: `This ${kind} could not be started`,
      description:
        "Validation failed before anything was sent to the airline. No money moved. Close this request, then the agent can submit again if it is still eligible.",
      statusLabel: "Could not start",
      tone: "danger",
      primary: [{ key: CHANGE_ACTION.decline, label: "Close this request" }],
      secondary: [{ key: CHANGE_ACTION.assign, label: "Assign to teammate" }],
    },
    settlement_failed: {
      title: "Wallet settlement failed",
      description:
        "Supplier/airline success is separate from money. The ticket change may already be done. Confirm re-issue if the new ticket is ready. Assign so finance can fix release/reversal. Do not change a successful change back to failed.",
      statusLabel: "Settlement failed — finance attention",
      tone: "danger",
      primary: [
        { key: CHANGE_ACTION.confirmReissue, label: "Confirm re-issue" },
        {
          key: CHANGE_ACTION.manualChange,
          label: "Record successful exchange",
        },
      ],
      secondary: [
        { key: CHANGE_ACTION.assign, label: "Assign to finance / teammate" },
      ],
    },
    failure_settled: {
      title: "Change failed — money settled",
      description:
        "The airline did not complete this change. Any reserved payment has been settled. Keep this as a failed change, not completed. Original ticket stays.",
      statusLabel: "Change failed — settled",
      tone: "danger",
      primary: [],
      secondary: [],
    },
    failure_settling: {
      title: "Funds need release / reversal",
      description:
        "The airline did not complete this change. Finance is releasing or reversing the reserved payment. Keep this as a failed change. Assign a teammate if this sits here too long.",
      statusLabel: "Reversing payment — finance",
      tone: "wait",
      primary: [],
      secondary: [
        { key: CHANGE_ACTION.assign, label: "Assign to finance / teammate" },
      ],
    },
    unknown: {
      title: isOpenTicketChange(data)
        ? "Open ticket request"
        : `${isDetails ? "Details" : "Date"} change request`,
      description:
        "Open the available actions below. If you are unsure, assign this to a teammate rather than guessing.",
      statusLabel: data?.status || "Unknown",
      tone: "info",
      primary: [{ key: CHANGE_ACTION.assign, label: "Assign to teammate" }],
      secondary: [],
    },
  };

  const result = {
    stage,
    kind,
    isDetails,
    ...(guidance[stage] || guidance.unknown),
  };
  const overlay = getChangeStatusOverlay(data, { stage, kind, isDetails });
  if (overlay) {
    if (overlay.title) result.title = overlay.title;
    if (overlay.description) result.description = overlay.description;
    if (overlay.statusLabel) result.statusLabel = overlay.statusLabel;
    if (overlay.tone) result.tone = overlay.tone;
  }
  const quoteLabel = getChangeQuoteActionLabel(data);
  result.primary = (result.primary || []).map((action) =>
    action.key === CHANGE_ACTION.quote
      ? { ...action, label: quoteLabel }
      : action,
  );
  result.secondary = (result.secondary || []).map((action) =>
    action.key === CHANGE_ACTION.quote
      ? { ...action, label: quoteLabel }
      : action,
  );
  const supplier = data?.supplier || data?.flightBooking?.supplier;
  if (!isGDSSupplier(supplier)) {
    result.secondary = (result.secondary || []).filter(
      (action) => action.key !== CHANGE_ACTION.gdsReissue,
    );
  }
  return result;
}

export function canSubmitChangeToAirline(data) {
  if (isOpenTicketChange(data)) return false;
  return ["needs_airline_submit", "send_failed"].includes(
    getChangeAdminStage(data),
  );
}

export function isChangeCloseRequestStage(data) {
  return [
    "validation_failed",
    "supplier_failed",
    "open_validation_failed",
    "failed",
  ].includes(getChangeAdminStage(data));
}

/**
 * Statuses where POST changeBooking/declineBySupplier is allowed (backend).
 * Date change (Flight), details change (PassengerInfo), open ticket (Open).
 * Team decline remains PATCH deny.
 */
export function canRecordChangeSupplierDecline(data) {
  if (!data) return false;
  const status = normalizeChangeStatus(data?.status);

  if (isDetailsChange(data)) {
    return [
      "namechangepaymentauthorised",
      "namechangepaymentauthorized",
      "namechangesubmittedtosupplier",
    ].includes(status);
  }

  if (isOpenTicketChange(data)) {
    return [
      "manualopenticketprocessing",
      "openticketprocessingwithsupplier",
      "openticketrequestsubmitted",
    ].includes(status);
  }

  // Date change (changeRequest: "Flight")
  return [
    "manualreviewrequired",
    "manualdatechangeprocessing",
    "autodatechangefailed",
    "autochangesubmissionfailed",
    "paymentauthorised",
    "paymentauthorized",
    "paymentnotrequired",
    "refunddue",
    "datechangesubmitting",
    "autochangesubmission",
    "submittedtosupplier",
    "supplierprocessing",
  ].includes(status);
}

/** Show airline vs team choice when supplier decline is allowed. */
export function shouldChooseChangeDeclineSource(data) {
  if (isChangeCloseRequestStage(data)) return false;
  return canRecordChangeSupplierDecline(data);
}

export function canReviewChange(data) {
  const stage = getChangeAdminStage(data);
  if (isOpenTicketChange(data)) {
    return stage === "needs_review";
  }
  return stage === "needs_review";
}

export function canEnterChangeQuote(data) {
  if (isOpenTicketChange(data)) return false;
  const stage = getChangeAdminStage(data);
  // DateChangeQuoteExpired / NameChangeQuoteExpired (and soft-expired name quotes) are terminal.
  if (stage === "quote_expired_closed") return false;
  // ManualReviewRequired / needs_review = Accept first. Quote only after.
  if (stage === "needs_review") return false;
  if (
    isChangeManualReviewStatus(data?.status) &&
    !isChangeAlreadyAccepted(data)
  ) {
    return false;
  }
  return [
    "needs_quote",
    "quote_expired",
    "awaiting_agent",
    "awaiting_payment",
    "auto_processing",
  ].includes(stage);
}

export function canCheckChangeFareRules(data) {
  if (!data || isOpenTicketChange(data)) return false;
  const booking = Array.isArray(data.flightBooking)
    ? data.flightBooking[0]
    : data.flightBooking || data.flightObj;
  return Boolean(
    booking?.bookingId ||
    booking?._id ||
    (booking?.formattedFareRules &&
      Object.keys(booking.formattedFareRules).length),
  );
}

export function canCheckChangeMessages(data) {
  if (!data) return false;
  return Boolean(data?._id);
}

export function getChangeMessageCount(data) {
  const raw = data?.conversation;
  if (!Array.isArray(raw)) return 0;
  return raw.filter(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      (Object.prototype.hasOwnProperty.call(entry, "message") ||
        Object.prototype.hasOwnProperty.call(entry, "image")),
  ).length;
}

export function canAdminPayChange(data) {
  if (isOpenTicketChange(data)) return false;
  if (hasPendingChangeBankTransferProof(data)) return false;
  return getChangeAdminStage(data) === "awaiting_payment";
}

export function canReviewChangeBankTransferProof(data) {
  if (isOpenTicketChange(data)) return false;
  // Only while a proof still needs ops — after confirm/authorise the
  // history stays on the request but this menu action goes away.
  if (isChangePaymentSecured(data)) return false;
  return hasPendingChangeBankTransferProof(data);
}

export function canManualChangeTicket(data) {
  if (isOpenTicketChange(data)) return false;
  return [
    "needs_airline_submit",
    "send_failed",
    "awaiting_supplier",
    "failed",
    "supplier_failed",
    "settlement_failed",
  ].includes(getChangeAdminStage(data));
}

export function canConfirmChangeReissue(data) {
  if (isOpenTicketChange(data)) return false;
  return ["needs_reissue", "failed", "settlement_failed"].includes(
    getChangeAdminStage(data),
  );
}

export function canDeclineChangeRequest(data) {
  const stage = getChangeAdminStage(data);
  if (isOpenTicketChange(data)) {
    return stage === "open_validation_failed";
  }
  // Backend POST declineBySupplier statuses (incl. Waiting on airline /
  // SubmittedToSupplier / SupplierProcessing) — Decline opens airline vs Ops.
  if (canRecordChangeSupplierDecline(data)) return true;
  return [
    "needs_review",
    "needs_quote",
    "awaiting_agent",
    "awaiting_payment",
    "quote_expired",
    "auto_processing",
    "validation_failed",
    "supplier_failed",
    "failed",
  ].includes(stage);
}

export function canAssignChangeRequest(data) {
  return ![
    "completed",
    "declined",
    "failure_settled",
    "quote_expired_closed",
    "open_completed",
    "open_not_eligible",
  ].includes(getChangeAdminStage(data));
}

export function canAddChangeRemark(data) {
  return canAssignChangeRequest(data);
}

export function canProceedOpenTicket(data) {
  if (!isOpenTicketChange(data)) return false;
  return ["open_processing", "open_failed", "open_needs_info"].includes(
    getChangeAdminStage(data),
  );
}

export function canRecordOpenedOpenTicket(data) {
  if (!isOpenTicketChange(data)) return false;
  return ["open_with_supplier", "open_failed", "open_needs_info"].includes(
    getChangeAdminStage(data),
  );
}

export function getChangeQuoteActionLabel(data) {
  const stage = getChangeAdminStage(data);
  if (stage === "auto_processing")
    return "Take over — enter quote from airline";
  if (stage === "quote_expired") return "Enter new quote";
  const code = normalizeChangeStatus(data?.status);
  if (
    hasChangeQuote(data) ||
    code === "datechangequoted" ||
    code === "namechangequoted" ||
    code === "namechangepaymentrequired" ||
    code === "datechangeaccepted" ||
    code === "paymentrequired"
  ) {
    return "Edit quote";
  }
  return "Enter quote from airline";
}

export function getChangeSubmitActionLabel() {
  return "Confirm sent to airline";
}

export function getChangeDeclineActionLabel(data) {
  return isChangeCloseRequestStage(data)
    ? "Close this request"
    : "Decline request";
}

export function getChangeAssignActionLabel(data) {
  return data?.tasks?.length ? "Reassign" : "Assign to teammate";
}

export function getChangeOverflowMenuActions(data) {
  const stage = getChangeAdminStage(data);
  const guidance = getChangeAdminGuidance(data);
  const items = [];

  function push(action) {
    if (!action?.key || items.some((item) => item.key === action.key)) return;
    items.push(action);
  }

  // ManualReviewRequired: slim list — Accept/Decline are inside Review.
  // View Request is added by FlightMenu above these items.
  if (stage === "needs_review") {
    if (canReviewChange(data)) {
      push({ key: CHANGE_ACTION.review, label: "Review this request" });
    }
    if (canRetryAutoChange(data)) {
      push({
        key: CHANGE_ACTION.retryAuto,
        label: getChangeRetryAutoActionLabel(),
      });
    }
    if (canCheckChangeMessages(data)) {
      const count = getChangeMessageCount(data);
      push({
        key: CHANGE_ACTION.messages,
        label: count > 0 ? `Check messages (${count})` : "Check messages",
      });
    }
    if (canAssignChangeRequest(data)) {
      push({
        key: CHANGE_ACTION.assign,
        label: getChangeAssignActionLabel(data),
      });
    }
    if (hasChangeSupplierResponse(data)) {
      push({
        key: CHANGE_ACTION.supplierResponse,
        label: "Supplier Response",
      });
    }
    return items;
  }

  // Messages sit just under "View Request" in the list menu.
  if (canCheckChangeMessages(data)) {
    const count = getChangeMessageCount(data);
    push({
      key: CHANGE_ACTION.messages,
      label: count > 0 ? `Check messages (${count})` : "Check messages",
    });
  }

  if (hasChangeSupplierResponse(data)) {
    push({
      key: CHANGE_ACTION.supplierResponse,
      label: "Supplier Response",
    });
  }

  if (canViewChangeSelectedOffer(data)) {
    push({
      key: CHANGE_ACTION.viewSelectedOffer,
      label: "View selected offer",
    });
  }

  if (canReviewChangeBankTransferProof(data)) {
    push({
      key: CHANGE_ACTION.reviewBankProof,
      label: "Review bank payment proof",
    });
  }

  [...(guidance.primary || []), ...(guidance.secondary || [])].forEach(push);

  if (canReviewChange(data)) {
    push({ key: CHANGE_ACTION.review, label: "Review this request" });
  }
  if (canRetryAutoChange(data)) {
    push({
      key: CHANGE_ACTION.retryAuto,
      label: getChangeRetryAutoActionLabel(),
    });
  }
  if (canProceedOpenTicket(data)) {
    push({
      key: CHANGE_ACTION.proceedOpen,
      label: "Proceed with the airline",
    });
  }
  if (canRecordOpenedOpenTicket(data)) {
    push({ key: CHANGE_ACTION.recordOpened, label: "Opened" });
  }
  if (canEnterChangeQuote(data)) {
    const quoteAction = {
      key: CHANGE_ACTION.quote,
      label: getChangeQuoteActionLabel(data),
    };
    const existing = items.find((item) => item.key === CHANGE_ACTION.quote);
    if (existing) existing.label = quoteAction.label;
    else items.push(quoteAction);
  }
  if (canAdminPayChange(data)) {
    push({
      key: CHANGE_ACTION.adminPay,
      label: "Pay from agent wallet",
    });
  }
  if (canSubmitChangeToAirline(data)) {
    push({
      key: CHANGE_ACTION.submit,
      label: getChangeSubmitActionLabel(),
    });
  }
  if (canManualChangeTicket(data)) {
    push({ key: CHANGE_ACTION.manualChange, label: "Manual change" });
  }
  if (canConfirmChangeReissue(data)) {
    push({
      key: CHANGE_ACTION.confirmReissue,
      label: "Confirm re-issue",
    });
  }
  if (canDeclineChangeRequest(data)) {
    push({
      key: CHANGE_ACTION.decline,
      label: getChangeDeclineActionLabel(data),
    });
  }
  if (canAssignChangeRequest(data)) {
    push({
      key: CHANGE_ACTION.assign,
      label: getChangeAssignActionLabel(data),
    });
  }
  if (canAddChangeRemark(data)) {
    push({ key: CHANGE_ACTION.remark, label: "Add remark" });
  }
  if (canCheckChangeFareRules(data)) {
    push({ key: CHANGE_ACTION.fareRules, label: "Check fare rules" });
  }

  return items;
}

export function getChangeSubmitWarningCopy(data) {
  const kind = getChangeKindLabel(data);
  return {
    phase: "warn",
    kind: "submit_warn",
    title: `Send this ${kind} to the airline`,
    text: "This tells Intraverse you have submitted the change to the airline. It does not finish the re-issue yet. A timeout is not an airline decline — come back and try again.",
  };
}

export function getChangeSubmitCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "submitted",
      title: "Marked as sent to the airline",
      text:
        msg && !/^success/i.test(msg)
          ? `${msg} Wait for the airline next. When the new ticket is ready, confirm the re-issue.`
          : "Wait for the airline next. When the new ticket is ready, confirm the re-issue so Intraverse matches the airline.",
    };
  }
  return {
    phase: "error",
    kind: "submit_failed",
    title: "We could not mark this as sent",
    text: `${msg || "The request failed or timed out"}. That is not an airline decline. Try again. Use Manual change only if you already finished it with the airline yourself.`,
  };
}

export function getChangeManualWarningCopy(data) {
  const kind = getChangeKindLabel(data);
  const stage = getChangeAdminStage(data);
  if (stage === "awaiting_supplier") {
    return {
      phase: "warn",
      kind: "manual_warn",
      title: "Only continue if the airline already finished this",
      text: `This request is still waiting on the airline. Manual change writes the new ticket into Intraverse. Use it only if you have confirmed the airline already completed this ${kind}. A timeout is not an airline decline.`,
    };
  }
  if (stage === "supplier_failed") {
    return {
      phase: "warn",
      kind: "manual_warn",
      title: "Only continue if the airline actually made this change",
      text: `The airline recorded a fail or decline. Manual change still writes a new ticket into Intraverse. Only continue if you have confirmed the airline did complete this ${kind}. Do not create a new quote on this same request.`,
    };
  }
  if (stage === "settlement_failed") {
    return {
      phase: "warn",
      kind: "manual_warn",
      title: "The airline change stays as it is",
      text: "Wallet settlement failed. This does not un-change the ticket. Use Manual change only to write the new ticket into Intraverse if it is not already there. Do not treat this as an airline decline.",
    };
  }
  return {
    phase: "warn",
    kind: "manual_warn",
    title: "Record a manual change",
    text: `Use this only if you already completed this ${kind} with the airline yourself. This writes the new ticket into Intraverse. If you have not sent it yet, go back and use Confirm sent to airline.`,
  };
}

export function getChangeReissueWarningCopy(data) {
  const whatChanges = isDetailsChange(data)
    ? "the new passenger names"
    : "the new travel dates";
  return {
    phase: "warn",
    kind: "reissue_warn",
    title: "This records a new ticket",
    text: `Confirm re-issue writes ${whatChanges} into Intraverse as a new ticket. Only continue if the airline has already reissued it. The original ticket stays in the audit trail. This is not a new quote and does not charge the agent again.`,
  };
}

export function getChangeReissueCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "reissued",
      title: "The new ticket is in Intraverse",
      text:
        msg && !/^success/i.test(msg)
          ? `${msg} This change is complete. The original itinerary is kept for audit.`
          : "This change is complete. The original itinerary is kept for audit. No further action is needed.",
    };
  }
  return {
    phase: "error",
    kind: "reissue_failed",
    title: "We could not write the new ticket",
    text: `${msg || "The request failed or timed out"}. That is not an airline decline. Try again, or assign this to a teammate. Do not create a new quote on this same request.`,
  };
}

export function getOpenTicketStartWarningCopy() {
  return {
    phase: "warn",
    kind: "open_start_warn",
    title: "This can be opened",
    text: "You are taking this to the airline by hand. There is no quote and no charge. The computer will not talk to the airline. Continue only if this ticket can stay open for later travel.",
  };
}

export function getOpenTicketStartCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "open_started",
      title: "Ops is processing this open ticket",
      text:
        msg && !/^success/i.test(msg)
          ? `${msg} Talk to the airline next. Cancel the current flights and keep the ticket OPEN. Do not refund it.`
          : "Talk to the airline next. Cancel the current flights and keep the ticket OPEN. Do not refund it.",
    };
  }
  return {
    phase: "error",
    kind: "open_start_failed",
    title: "We could not start this",
    text: `${msg || "The request failed or timed out"}. That is not an airline decline. Try again, or assign this to a teammate.`,
  };
}

export function getOpenTicketProceedWarningCopy() {
  return {
    phase: "warn",
    kind: "open_proceed_warn",
    title: "Proceed with the airline",
    text: "You will work this with the airline by hand. Cancel the current flights and keep the ticket OPEN. The computer will not talk to the airline. There is no quote and no charge. Continue only if this ticket can be opened.",
  };
}

export function getOpenTicketProceedCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "open_proceeded",
      title: "Working with the airline",
      text:
        msg && !/^success/i.test(msg)
          ? `${msg} Finish it with the airline, then record the result: opened, airline said no, or could not finish. A timeout is not an airline decline.`
          : "Finish it with the airline, then record the result: opened, airline said no, or could not finish. A timeout is not an airline decline.",
    };
  }
  return {
    phase: "error",
    kind: "open_proceed_failed",
    title: "We could not move this forward",
    text: `${msg || "The request failed or timed out"}. That is not an airline decline. Try again, or assign this to a teammate.`,
  };
}

export function getOpenTicketOpenedWarningCopy() {
  return {
    phase: "warn",
    kind: "open_opened_warn",
    title: "Record opened",
    text: "Use this only if the airline already opened the ticket. This writes OPEN into Intraverse. The current flights should be off and the unused ticket kept for later. It is not a refund and not a cancel. There is no quote and no charge. Do not click this if the airline is still working.",
  };
}

export function getOpenTicketOpenedCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "open_opened",
      title: "Ticket is OPEN",
      text:
        msg && !/^success/i.test(msg)
          ? `${msg} The current flights are off. The ticket stays for a later date change. No money moved.`
          : "The current flights are off. The ticket stays for a later date change. No money moved. This request is complete.",
    };
  }
  return {
    phase: "error",
    kind: "open_opened_failed",
    title: "We could not record this as opened",
    text: `${msg || "The request failed or timed out"}. That is not an airline decline. Try again, or assign this to a teammate.`,
  };
}
